import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"; // Ensure useEffect is imported
import { generateManifestPdf } from "./lib/manifestPdf";
import { API_BASE_URL } from "./config";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, AdaptiveDpr } from "@react-three/drei";
import * as THREE from "three";
import * as XLSX from "xlsx";
import { Html } from "@react-three/drei";
import AxleHUD from "./components/AxleHUD";

/** Dimensions of the vehicles. */
export const VEHICLE_DIMENSIONS = {
  trailer: {
    width: 2.45,
    height: 2.6,
    depth: 12,
  },
  truck: {
    width: 2.45,
    height: 2.6,
    depth: 8.4,
  },
};
/** Default truck dimensions for compatibility. */
export const TRUCK_DIMENSIONS = VEHICLE_DIMENSIONS.trailer;

/** Renders the truck boundary as a wireframe box. */
function Truck({ vehicleType = "trailer" }) {
  const dimensions = VEHICLE_DIMENSIONS[vehicleType];

  return (
    <group>
      <mesh position={[0, dimensions.height / 2, 0]}>
        <boxGeometry
          args={[dimensions.width, dimensions.height, dimensions.depth]}
        />
        <meshStandardMaterial color="gray" wireframe />
      </mesh>
    </group>
  );
}

/** A grid floor for visual reference (optional). */
function Grid() {
  const gridHelper = new THREE.GridHelper(
    TRUCK_DIMENSIONS.depth,
    15,
    "white",
    "gray"
  );
  return <primitive object={gridHelper} position={[0, 0.01, 0]} />;
}

// --------------------------------------------------
// Collision & snapping logic
// --------------------------------------------------
function getEffectiveDims({ width, depth, height }, rotationDeg) {
  const r = (rotationDeg % 360 + 360) % 360;
  const rotated90 = r === 90 || r === 270;
  return rotated90
    ? { width: depth, depth: width, height }
    : { width, depth, height };
}

function boxesOverlapXZStrict(x1, z1, w1, d1, x2, z2, w2, d2) {
  const EPS = 1e-5;
  const overlapX = Math.abs(x1 - x2) + EPS < (w1 + w2) / 2;
  const overlapZ = Math.abs(z1 - z2) + EPS < (d1 + d2) / 2;
  return overlapX && overlapZ;
}

// Check if two pallets (projected in XZ) are "adjacent" (faces nearly touching)
// and overlap along the orthogonal axis. Used for hazardous adjacency rules.
function boxesAdjacentXZ(nx, nz, wN, dN, ox, oz, wO, dO, gap = 0.05) {
  const dx = Math.abs(nx - ox);
  const dz = Math.abs(nz - oz);
  const sumHalfW = (wN + wO) / 2;
  const sumHalfD = (dN + dO) / 2;
  // Adjacent along X if faces are within `gap` and overlap in Z
  const adjX = Math.abs(dx - sumHalfW) <= gap && dz < sumHalfD;
  // Adjacent along Z if faces are within `gap` and overlap in X
  const adjZ = Math.abs(dz - sumHalfD) <= gap && dx < sumHalfW;
  return adjX || adjZ;
}

// Clamp a position to stay within vehicle bounds, respecting rotation
function clampPositionToBounds(position, dimensions, vehicleType = 'trailer', rotationDeg = 0) {
  const dims = VEHICLE_DIMENSIONS[vehicleType];
  const { width, depth, height } = getEffectiveDims(dimensions, rotationDeg || 0);
  const halfTruckW = dims.width / 2;
  const halfTruckD = dims.depth / 2;
  const truckH = dims.height;
  const halfW = width / 2;
  const halfD = depth / 2;
  const halfH = height / 2;
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const x = clamp(position[0], -halfTruckW + halfW, halfTruckW - halfW);
  const y = clamp(position[1], halfH, truckH - halfH);
  const z = clamp(position[2], -halfTruckD + halfD, halfTruckD - halfD);
  return [x, y, z];
}

// 1. Add a boundary check function to ensure pallets stay inside the vehicle
function isWithinVehicleBounds(position, dimensions, vehicleType = 'trailer', rotationDeg = 0) {
  const vehicleDimensions = VEHICLE_DIMENSIONS[vehicleType];
  // Respect rotation when computing effective footprint
  const { width, depth, height } = getEffectiveDims(dimensions, rotationDeg || 0);
  const palletHalfWidth = width / 2;
  const palletHalfDepth = depth / 2;
  const palletHalfHeight = height / 2;

  const vehicleHalfWidth = vehicleDimensions.width / 2;
  const vehicleHalfDepth = vehicleDimensions.depth / 2;
  const vehicleHeight = vehicleDimensions.height;

  // Tiny epsilon to avoid premature false due to float noise when flush to walls
  const EPS = 1e-6;

  // Check X-axis (width)
  const withinX = Math.abs(position[0]) + palletHalfWidth <= vehicleHalfWidth + EPS;

  // Check Z-axis (depth)
  const withinZ = Math.abs(position[2]) + palletHalfDepth <= vehicleHalfDepth + EPS;

  // Check Y-axis (height) - bottom of pallet must be >= 0, top must be <= vehicle height
  const withinY = position[1] - palletHalfHeight >= -EPS &&
                  position[1] + palletHalfHeight <= vehicleHeight + EPS;

  return withinX && withinZ && withinY;
}

// 2. Update the canMoveToPosition function to include boundary checks
function canMoveToPosition(newPosition, pallet, allPallets, currentIndex, vehicleType = 'trailer') {
  // First check if position is within vehicle bounds
  if (!isWithinVehicleBounds(newPosition, pallet.dimensions, vehicleType, pallet.rotation)) {
    return false;
  }
  
  // Then check for collisions with other pallets
  const [nx, ny, nz] = newPosition;
  const { width: wN, depth: dN, height: hN } = getEffectiveDims(
    pallet.dimensions,
    pallet.rotation
  );
  const bottomOfNew = ny - hN / 2;
  const topOfNew = ny + hN / 2;
  const TOLERANCE = 0.01; // 1 cm margin; adjust as needed

  for (let i = 0; i < allPallets.length; i++) {
    if (i === currentIndex) continue;
  const other = allPallets[i];
  if (!other || !other.position || !other.dimensions) continue;
    
    const [ox, oy, oz] = other.position;
    const { width: wO, depth: dO, height: hO } = getEffectiveDims(
      other.dimensions,
      other.rotation
    );

    if (boxesOverlapXZStrict(nx, nz, wN, dN, ox, oz, wO, dO)) {
      const topOfOther = oy + hO / 2;
      const bottomOfOther = oy - hO / 2;
      // Allow a small margin so that touching surfaces are okay
      if (topOfNew > bottomOfOther + TOLERANCE && bottomOfNew < topOfOther - TOLERANCE) {
        return false;
      }
    }
  }
  // Hazardous adjacency no longer blocks placement; a warning will be shown elsewhere.
  return true;
}

// Explain why a position is invalid for a given pallet
function explainInvalidPosition(newPosition, pallet, allPallets, currentIndex, vehicleType = 'trailer') {
  const dims = VEHICLE_DIMENSIONS[vehicleType];
  const { width: wN, depth: dN, height: hN } = getEffectiveDims(pallet.dimensions, pallet.rotation || 0);
  const [nx, ny, nz] = newPosition;
  const halfTruckW = dims.width / 2;
  const halfTruckD = dims.depth / 2;
  const halfW = wN / 2;
  const halfD = dN / 2;
  const bottom = ny - hN / 2;
  const top = ny + hN / 2;

  // Bounds reasons
  if (Math.abs(nx) + halfW > halfTruckW) {
    return nx < 0 ? "Out of bounds: beyond left wall" : "Out of bounds: beyond right wall";
  }
  if (Math.abs(nz) + halfD > halfTruckD) {
    return nz < 0 ? "Out of bounds: beyond front wall" : "Out of bounds: beyond back door";
  }
  if (bottom < 0) return "Out of bounds: below floor";
  if (top > dims.height) return "Out of bounds: exceeds vehicle height";

  // Collision reasons (consider all pallets in vehicle)
  for (let i = 0; i < allPallets.length; i++) {
    if (i === currentIndex) continue;
    const other = allPallets[i];
    if (!other || !other.position || !other.dimensions) continue;
    const [ox, oy, oz] = other.position || [0, 0, 0];
    const { width: wO, depth: dO, height: hO } = getEffectiveDims(other.dimensions, other.rotation || 0);
    if (boxesOverlapXZStrict(nx, nz, wN, dN, ox, oz, wO, dO)) {
      const topO = oy + hO / 2;
      const bottomO = oy - hO / 2;
      const TOL = 0.01;
      if (top > bottomO + TOL && bottom < topO - TOL) {
        const label = other.name || other.id || "another pallet";
        return `Blocked: collides with ${label}`;
      }
    }
  }
  return "Placement blocked";
}

// Returns a warning string if the newPosition creates hazardous adjacency, otherwise null.
function getHazardAdjacencyWarning(newPosition, pallet, allPallets, currentIndex) {
  if (!newPosition || !pallet) return null;
  const [nx, ny, nz] = newPosition;
  const { width: wN, depth: dN, height: hN } = getEffectiveDims(pallet.dimensions, pallet.rotation || 0);
  const bottomOfNew = ny - hN / 2;
  const topOfNew = ny + hN / 2;
  for (let i = 0; i < allPallets.length; i++) {
    if (i === currentIndex) continue;
    const other = allPallets[i];
    if (!other) continue;
    const [ox, oy, oz] = other.position || [0, 0, 0];
    const { width: wO, depth: dO, height: hO } = getEffectiveDims(other.dimensions, other.rotation || 0);
    const topO = oy + hO / 2;
    const bottomO = oy - hO / 2;
    const TOL = 0.01;
    const a1 = (pallet.attribute || 'none').toLowerCase();
    const a2 = (other.attribute || 'none').toLowerCase();
    const conflict = (a1 === 'dangerous' && (a2 === 'food' || a2 === 'medicine')) ||
                     (a2 === 'dangerous' && (a1 === 'food' || a1 === 'medicine'));
    const overlapYBand = (topOfNew > bottomO + TOL && bottomOfNew < topO - TOL);
    if (conflict && overlapYBand && boxesAdjacentXZ(nx, nz, wN, dN, ox, oz, wO, dO, 0.05)) {
      const label = other.name || other.id || 'another pallet';
      if (a1 === 'dangerous') return `Warning: dangerous goods touching ${a2} (${label})`;
      if (a2 === 'dangerous') return `Warning: ${a1} touching dangerous goods (${label})`;
    }
    const STACK_GAP = 0.05;
    if (boxesOverlapXZStrict(nx, nz, wN, dN, ox, oz, wO, dO)) {
      const newAboveOther = Math.abs(bottomOfNew - topO) <= STACK_GAP && bottomOfNew >= topO - STACK_GAP;
      const newBelowOther = Math.abs(bottomO - topOfNew) <= STACK_GAP && bottomO >= topOfNew - STACK_GAP;
      if (conflict && (newAboveOther || newBelowOther)) {
        const label = other.name || other.id || 'another pallet';
        if (a1 === 'dangerous') return `Warning: dangerous goods stacked with ${a2} (${label})`;
        if (a2 === 'dangerous') return `Warning: ${a1} stacked with dangerous goods (${label})`;
      }
      const HEAVY_LIMIT = 500;
      if (newAboveOther && (pallet.weight || 0) > HEAVY_LIMIT) {
        const baseLabel = other.name || other.id || 'another pallet';
        return `Warning: heavy pallet (${pallet.weight}kg) stacked on ${baseLabel}`;
      }
    }
  }
  return null;
}

const ATTRIBUTE_COLORS = {
  medicine: "#00BFFF", // Deep Sky Blue
  dangerous: "#FF4500", // Orange Red
  food: "#32CD32",     // Lime Green
  none: "#FFD700",     // Gold for generic pallets
};

function edgeSnapX(candidateX, pallet, pallets, index, vehicleType = 'trailer', smartSnap = false, includeWalls = true) {
  const edgesX = [];
  const vehicleDimensions = VEHICLE_DIMENSIONS[vehicleType];
  const halfTruckW = vehicleDimensions.width / 2;
  if (includeWalls) edgesX.push(-halfTruckW, halfTruckW);

  for (let i = 0; i < pallets.length; i++) {
    if (i === index) continue;
  const other = pallets[i];
  if (!other || !other.position || !other.dimensions) continue;
    
    const [ox] = other.position;
    const { width: wO } = getEffectiveDims(other.dimensions, other.rotation);
    edgesX.push(ox - wO / 2, ox + wO / 2);
  }

  const { width: wN } = getEffectiveDims(pallet.dimensions, pallet.rotation);
  const newLeft = candidateX - wN / 2;
  const newRight = candidateX + wN / 2;

  let bestX = candidateX;
  let minDist = Infinity;
  const SNAP_THRESHOLD = smartSnap ? 0.25 : 0.1;

  for (const ex of edgesX) {
    const distLeft = Math.abs(newLeft - ex);
    const distRight = Math.abs(newRight - ex);
    const d = Math.min(distLeft, distRight);
    if (d < minDist && d <= SNAP_THRESHOLD) {
      minDist = d;
      if (distLeft < distRight) {
        bestX = ex + wN / 2;
      } else {
        bestX = ex - wN / 2;
      }
    }
  }
  return bestX;
}
// Snap along the Z axis to nearby pallet edges or walls
function edgeSnapZ(candidateZ, pallet, pallets, index, vehicleType = 'trailer', smartSnap = false, includeWalls = true) {
  const edgesZ = [];
  const vehicleDimensions = VEHICLE_DIMENSIONS[vehicleType];
  const halfTruckD = vehicleDimensions.depth / 2;
  if (includeWalls) edgesZ.push(-halfTruckD, halfTruckD);

  for (let i = 0; i < pallets.length; i++) {
    if (i === index) continue;
  const other = pallets[i];
  if (!other || !other.position || !other.dimensions) continue;
    const [, , oz] = other.position;
    const { depth: dO } = getEffectiveDims(other.dimensions, other.rotation);
    edgesZ.push(oz - dO / 2, oz + dO / 2);
  }

  const { depth: dN } = getEffectiveDims(pallet.dimensions, pallet.rotation);
  const newFront = candidateZ - dN / 2;
  const newBack = candidateZ + dN / 2;

  let bestZ = candidateZ;
  let minDist = Infinity;
  const SNAP_THRESHOLD = smartSnap ? 0.25 : 0.1;
  for (const ez of edgesZ) {
    const distFront = Math.abs(newFront - ez);
    const distBack = Math.abs(newBack - ez);
    const d = Math.min(distFront, distBack);
    if (d < minDist && d <= SNAP_THRESHOLD) {
      minDist = d;
      if (distFront < distBack) {
        bestZ = ez + dN / 2;
      } else {
        bestZ = ez - dN / 2;
      }
    }
  }
  return bestZ;
}

// Snap to pallets/walls in X/Z and find the nearest valid stacking height (Y)
function edgeSnapToPallets(
  basePos,
  pallet,
  pallets,
  index,
  vehicleType = 'trailer',
  smartSnap = false,
  preferredY = null,
  includeWalls = true
) {
  if (!basePos || !pallet) return null;
  const [bx, by, bz] = basePos;

  // Snap X/Z first
  const cx = edgeSnapX(bx, pallet, pallets, index, vehicleType, smartSnap, includeWalls);
  const cz = edgeSnapZ(bz, pallet, pallets, index, vehicleType, smartSnap, includeWalls);

  // Build surfaces to consider for stacking
  const { width: wN, depth: dN, height: hN } = getEffectiveDims(pallet.dimensions, pallet.rotation || 0);
  const surfaces = [0]; // floor
  const overlapSupporters = [];
  for (let i = 0; i < pallets.length; i++) {
    if (i === index) continue;
  const other = pallets[i];
  if (!other || !other.position || !other.dimensions) continue;
    const [ox, oy, oz] = other.position || [0, 0, 0];
    const { width: wO, depth: dO, height: hO } = getEffectiveDims(other.dimensions, other.rotation || 0);
    if (boxesOverlapXZStrict(cx, cz, wN, dN, ox, oz, wO, dO)) {
      overlapSupporters.push(other);
      surfaces.push(oy + hO / 2);
    }
  }
  surfaces.sort((a, b) => a - b);

  // Find viable Y bands between surfaces
  const TOL = 0.1;
  const viableYs = [];
  for (let i = 0; i < surfaces.length; i++) {
    const bottom = surfaces[i];
    const top = i + 1 < surfaces.length ? surfaces[i + 1] : VEHICLE_DIMENSIONS[vehicleType].height;
    if (top - bottom >= hN + TOL) {
      const cy = bottom + hN / 2;
      const pos = [cx, cy, cz];
      if (canMoveToPosition(pos, pallet, pallets, index, vehicleType)) viableYs.push(cy);
    }
  }
  if (viableYs.length === 0) return null;
  let chosenY = viableYs[0];
  if (preferredY !== null && Number.isFinite(preferredY)) {
    let best = Infinity;
    for (const cy of viableYs) {
      const d = Math.abs(cy - preferredY);
      if (d < best) {
        best = d;
        chosenY = cy;
      }
    }
  }
  const finalPos = [cx, chosenY, cz];
  return canMoveToPosition(finalPos, pallet, pallets, index, vehicleType) ? finalPos : null;
}

// Smart fallback: search small local offsets for a viable placement when blocked
function findNearbyClearPosition(basePos, pallet, pallets, index, vehicleType = 'trailer', includeWalls = true) {
  const [bx, by, bz] = basePos;
  const xOffsets = [0, -0.05, 0.05, -0.1, 0.1, -0.15, 0.15];
  const zOffsets = [0, -0.05, 0.05, -0.1, 0.1];
  for (const xo of xOffsets) {
    for (const zo of zOffsets) {
      if (xo === 0 && zo === 0) continue;
      const candidate = [bx + xo, by, bz + zo];
  if (!isWithinVehicleBounds(candidate, pallet.dimensions, vehicleType, pallet.rotation)) continue;
  const snapped = edgeSnapToPallets(candidate, pallet, pallets, index, vehicleType, true, by, includeWalls);
      if (snapped && canMoveToPosition(snapped, pallet, pallets, index, vehicleType)) return snapped;
    }
  }
  return null;
}

/** Single Pallet as a 3D object. */
function Pallet({
  index,
  id,
  pallets,
  position,
  rotation,
  dimensions,
  attribute,
  notToploadable,
  name,
  locked,
  onDrag,
  setCameraActive,
  setSelectedPalletId,
  selectedPalletId,
  showDangerousOnly,
  vehicleType,
  fused,
  smartSnapEnabled,
  onMoveError,
  // New optional presentation controls
  overrideVisible,
  highlight,
}) {
  const [showInfo, setShowInfo] = useState(false);
  const { camera, gl } = useThree();
  const isSelected = selectedPalletId === id;
  const [dragging, setDragging] = useState(false);
  const [trackingDrag, setTrackingDrag] = useState(false);
  const dragOffsetRef = React.useRef([0, 0]); // [dx, dz] between pointer hit and pallet center
  const initYRef = React.useRef(null);
  const rafIdRef = React.useRef(null);
  const pendingPosRef = React.useRef(null);
  const lastAppliedRef = React.useRef(position);
  const downInfoRef = React.useRef({ t: 0, x: 0, z: 0 });

  useEffect(() => {
    lastAppliedRef.current = position;
  }, [position]);
  
  // Add safety checks for pallets array and index
  const pallet = pallets && pallets[index];
  const weight = pallet ? pallet.weight || 0 : 0;
  const geometry = useMemo(
    () =>
      new THREE.BoxGeometry(
        dimensions.width,
        dimensions.height,
        dimensions.depth
      ),
    [dimensions.width, dimensions.height, dimensions.depth]
  );
  const edgesGeometry = useMemo(() => new THREE.EdgesGeometry(geometry), [geometry]);

  const baseColor = highlight ? "#ff2d2d" : (ATTRIBUTE_COLORS[attribute] || "brown");
 // ----- vertical “fused” frame (orange with black outline) ----------------
  const fusedLineMat     = useMemo(() => new THREE.LineBasicMaterial({ color: "#FFA500" }), []);
  const fusedOutlineMat  = useMemo(() => new THREE.LineBasicMaterial({ color: "black"    }), []);

  // Rectangle in the Y-Z plane at X = 0 (centre of pallet)
  const fusedRingGeo = useMemo(() => {
    if (!fused) return null;
    const hw = dimensions.width  / 2 + 0.0005;   // tiny offset to avoid z-fighting
    const hh = dimensions.height / 2 + 0.0005;
    const verts = new Float32Array([
       -hw, -hh, 0,
         hw, -hh, 0,
         hw,  hh, 0,
        -hw,  hh, 0,
        -hw, -hh, 0,
    ]);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(verts, 3));
    return g;
  }, [fused, dimensions.width, dimensions.height]);

  // removed duplicate isSelected based on index
  const isDangerous = attribute === "dangerous";

  // Detect shim flag from backend (both snake_case and legacy camel)
  const hasShim = !!(pallets?.[index]?.tom_halvpall || pallets?.[index]?.tomhalvpall);
  const SHIM_H = 0.15;      // matches backend TOMHALVPALL_H
  const SHIM_T = 0.03;      // bar thickness for visual U-frame
  // Positions for U-shaped frame bars relative to pallet center
  const shimY = (dimensions.height / 2) - (SHIM_H / 2) + 0.0005; // sit at top, slight lift to avoid z-fighting
  const shimFrontZ = -(dimensions.depth / 2) + (SHIM_T / 2) + 0.001;
  const shimLeftX  = -(dimensions.width / 2) + (SHIM_T / 2) + 0.001;
  const shimRightX =  (dimensions.width / 2) - (SHIM_T / 2) - 0.001;
  const sideDepth  = Math.max(0, dimensions.depth - SHIM_T);

  useEffect(() => {
    function handleKeyDown(e) {
  if (selectedPalletId !== id) return;
      if (locked) return;
      if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === " "
      ) {
        e.preventDefault();
      }

      const MOVE_STEP = 0.25;
      let moveX = 0,
        moveZ = 0,
        moveY = 0,
        rotateDelta = 0;

      if (e.key === "ArrowLeft") moveX = -MOVE_STEP;
      if (e.key === "ArrowRight") moveX = MOVE_STEP;
      if (e.key === "ArrowUp") moveZ = -MOVE_STEP;
      if (e.key === "ArrowDown") moveZ = MOVE_STEP;
      if (e.key === " ") moveY = dimensions.height;
      if (e.key.toLowerCase() === "r") rotateDelta = 90;

      if (rotateDelta) {
        const newRotation = (rotation + rotateDelta) % 360;
        const testPallet = { ...pallets[index], rotation: newRotation };
  const snappedPos = edgeSnapToPallets(position, testPallet, pallets, index, vehicleType, smartSnapEnabled, position[1]);
        if (snappedPos && canMoveToPosition(snappedPos, testPallet, pallets, index, vehicleType)) {
          onDrag(index, snappedPos, newRotation);
        }
        return;
      }

      if (moveX || moveZ || moveY) {
        const testPallet = { ...pallets[index] };
        const candidatePos = [
          position[0] + moveX,
          position[1] + moveY,
          position[2] + moveZ,
        ];
        
        // Clamp to bounds first so we can snap flush to walls instead of erroring early
        const clampedPos = clampPositionToBounds(
          candidatePos,
          testPallet.dimensions,
          vehicleType,
          testPallet.rotation
        );
        const EPS = 1e-6;
        const wasClamped =
          Math.abs(clampedPos[0] - candidatePos[0]) > EPS ||
          Math.abs(clampedPos[1] - candidatePos[1]) > EPS ||
          Math.abs(clampedPos[2] - candidatePos[2]) > EPS;

        let snappedPos = edgeSnapToPallets(
          clampedPos,
          testPallet,
          pallets,
          index,
          vehicleType,
          smartSnapEnabled,
          clampedPos[1]
        );
        if (
          !(snappedPos && canMoveToPosition(snappedPos, testPallet, pallets, index, vehicleType)) &&
          smartSnapEnabled
        ) {
          const fallback = findNearbyClearPosition(clampedPos, testPallet, pallets, index, vehicleType);
          if (fallback) snappedPos = fallback;
        }
        if (snappedPos && canMoveToPosition(snappedPos, testPallet, pallets, index, vehicleType)) {
          onDrag(index, snappedPos, rotation);
          const hazard = getHazardAdjacencyWarning(snappedPos, testPallet, pallets, index);
          if (hazard && onMoveError) onMoveError(hazard);
          // If we hit the wall and got clamped, still inform the user why further movement is blocked
          if (wasClamped && onMoveError) {
            onMoveError(explainInvalidPosition(candidatePos, testPallet, pallets, index, vehicleType));
          }
        } else {
          // Use the original attempted position for a clearer reason (e.g., beyond right/front wall)
          if (onMoveError) onMoveError(explainInvalidPosition(candidatePos, testPallet, pallets, index, vehicleType));
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedPalletId, id, index, position, rotation, dimensions, pallets, onDrag, locked, vehicleType, smartSnapEnabled]);

  const handlePointerDown = useCallback(
    (e) => {
      e.stopPropagation();
  setSelectedPalletId(id);
      if (!locked) {
        // Compute world intersection on ground at pointer down
        const rect = gl.domElement.getBoundingClientRect();
        const ndc = new THREE.Vector2(
          ((e.clientX - rect.left) / rect.width) * 2 - 1,
          -((e.clientY - rect.top) / rect.height) * 2 + 1
        );
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(ndc, camera);
        const ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
        const intersect = new THREE.Vector3();
        const hit = raycaster.ray.intersectPlane(ground, intersect);
        const px = hit ? intersect.x : position[0];
        const pz = hit ? intersect.z : position[2];
        dragOffsetRef.current = [position[0] - px, position[2] - pz];
        initYRef.current = position[1];
        downInfoRef.current = { t: performance.now(), x: px, z: pz };
        // defer setting dragging until we see movement beyond a small threshold
        setTrackingDrag(true);
      }
    },
  [index, id, setSelectedPalletId, locked, position, setCameraActive, gl, camera]
  );

  const handleDrag = useCallback((newPosition) => {
    if (locked) return;
    // First check if within vehicle boundaries
    if (!isWithinVehicleBounds(newPosition, dimensions, vehicleType, rotation)) {
      onMoveError && onMoveError(
        explainInvalidPosition(
          newPosition,
          { dimensions, rotation, unit_id: pallets[index]?.unit_id },
          pallets,
          index,
          vehicleType
        )
      );
      return;
    }
    // Snap without wall edges during drag
    let snappedPos = edgeSnapToPallets(
      newPosition,
      { dimensions, rotation },
      pallets,
      index,
      vehicleType,
      smartSnapEnabled,
      newPosition[1],
      false
    );
    if (
      !(
        snappedPos &&
        canMoveToPosition(snappedPos, { dimensions, rotation }, pallets, index, vehicleType)
      ) && smartSnapEnabled
    ) {
      const fallback = findNearbyClearPosition(
        newPosition,
        { dimensions, rotation },
        pallets,
        index,
        vehicleType,
        false
      );
      if (fallback) snappedPos = fallback;
    }
    if (
      snappedPos &&
      canMoveToPosition(snappedPos, { dimensions, rotation }, pallets, index, vehicleType)
    ) {
      // Deduplicate tiny updates
      const prev = lastAppliedRef.current;
      const EPS = 1e-3;
      if (
        !prev ||
        Math.abs((snappedPos[0] ?? 0) - (prev[0] ?? 0)) > EPS ||
        Math.abs((snappedPos[1] ?? 0) - (prev[1] ?? 0)) > EPS ||
        Math.abs((snappedPos[2] ?? 0) - (prev[2] ?? 0)) > EPS
      ) {
  onDrag(index, snappedPos, rotation);
      const hazard = getHazardAdjacencyWarning(snappedPos, { dimensions, rotation }, pallets, index);
  if (hazard && onMoveError) onMoveError(hazard);
        lastAppliedRef.current = snappedPos;
      }
    } else {
      onMoveError && onMoveError(
        explainInvalidPosition(
          newPosition,
          { dimensions, rotation, unit_id: pallets[index]?.unit_id },
          pallets,
          index,
          vehicleType
        )
      );
    }
  }, [index, dimensions, rotation, pallets, onDrag, locked, vehicleType, smartSnapEnabled, onMoveError]);

  useEffect(() => {
    if (!trackingDrag) return;
    const onMove = (ev) => {
      // Convert screen pointer to world XZ via raycasting and apply offset
      const rect = gl.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((ev.clientX - rect.left) / rect.width) * 2 - 1,
        -((ev.clientY - rect.top) / rect.height) * 2 + 1
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(ndc, camera);
      const ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
      const intersect = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(ground, intersect)) {
        const [dx, dz] = dragOffsetRef.current;
        const candidate = [intersect.x + dx, initYRef.current ?? position[1], intersect.z + dz];
        // start dragging if moved beyond small threshold
        if (!dragging) {
          const d0 = downInfoRef.current;
          const dist = Math.hypot((intersect.x - d0.x) || 0, (intersect.z - d0.z) || 0);
          if (dist > 0.01) {
            setDragging(true);
            setCameraActive && setCameraActive(false);
          }
        }
        // Batch updates to animation frames to reduce churn
        pendingPosRef.current = candidate;
        if (!rafIdRef.current) {
          rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            const p = pendingPosRef.current;
            if (p) handleDrag(p);
          });
        }
      }
    };
    const onUp = () => {
      setDragging(false);
      setTrackingDrag(false);
      setCameraActive && setCameraActive(true);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      pendingPosRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [trackingDrag, dragging, gl, camera, handleDrag, position, setCameraActive]);

  

  const rotY = useMemo(() => (rotation * Math.PI) / 180, [rotation]);

  const isVisible = (overrideVisible !== undefined) ? !!overrideVisible : (!showDangerousOnly || isDangerous);

  // Return null if pallet doesn't exist - after all hooks have been called
  if (!pallet) {
    console.warn(`Pallet component: No pallet found at index ${index}, pallets array length: ${pallets ? pallets.length : 'undefined'}`);
    return null;
  }

  return (
    <group
      position={position}
      rotation={[0, rotY, 0]}
      visible={isVisible}
    >
      {/* Invisible hitbox to make clicking reliable */}
      <mesh onPointerDown={handlePointerDown}>
        <boxGeometry args={[dimensions.width + 0.04, dimensions.height + 0.04, dimensions.depth + 0.04]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} />
      </mesh>
      <mesh geometry={geometry}>
        <meshStandardMaterial
          color={baseColor}
          emissive={baseColor}
          emissiveIntensity={isSelected ? 0.7 : 0.2}
        />
      </mesh>

      {/* black outline around every pallet */}
      <lineSegments
        geometry={edgesGeometry}
        material={new THREE.LineBasicMaterial({ color: locked ? "#16a34a" : "black" })}
      />

      {/* ────────────────────────────────
          orange “belt” + black outline
         ──────────────────────────────── */}
      {fused && fusedRingGeo && (
        <>
          {/* thin black outline: slightly larger scale so it peeks out */}
          <line
            geometry={fusedRingGeo}
            material={fusedOutlineMat}
            scale={[1.03, 1.03, 1]}   /* expand X & Y only */
            renderOrder={998}
          />
          {/* orange belt itself */}
          <line
            geometry={fusedRingGeo}
            material={fusedLineMat}
            renderOrder={999}
          />
        </>
      )}
      {/* tom_halvpall U-shaped shim (black) sitting on top of shorter half */}
      {hasShim && (
        <>
          {/* Front bar across width */}
          <mesh position={[0, shimY, shimFrontZ]}>
            <boxGeometry args={[dimensions.width, SHIM_T, SHIM_T]} />
            <meshStandardMaterial color="#000000" />
          </mesh>
          {/* Left side bar along depth, open at back */}
          <mesh position={[shimLeftX, shimY, 0]}>
            <boxGeometry args={[SHIM_T, SHIM_T, sideDepth]} />
            <meshStandardMaterial color="#000000" />
          </mesh>
          {/* Right side bar along depth, open at back */}
          <mesh position={[shimRightX, shimY, 0]}>
            <boxGeometry args={[SHIM_T, SHIM_T, sideDepth]} />
            <meshStandardMaterial color="#000000" />
          </mesh>
        </>
      )}

      {/* Locked badge above pallet */}
      {locked && (
        <Html
          position={[0, (dimensions.height / 2) + 0.08, 0]}
          center
          distanceFactor={12}
          transform
          occlude
        >
          <div className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-600 text-white shadow select-none">
            🔒 Locked
          </div>
        </Html>
      )}
    </group>
  );
}

const TruckLoadingScene = React.forwardRef(function TruckLoadingScene({
  lines,
  setLines,
  activeLineId,
  setActiveLineId,
  trucks,
  setTrucks,
  activeTruckIndex,
  setActiveTruckIndex,
  unplacedPallets,
  setUnplacedPallets,
  truckId,
  vehicleType,
  truckName,
  onRowWeightsUpdate, // Add callback for row weights
  getDimsForTruck: getDimsForTruckProp,
  globalRowWeights: globalRowWeightsProp,
  onSelectedPalletChange,
  showGhostsOnly,
  // Loader-only visibility mode: show only placed pallets and the next-to-place
  loadingMode = false,
  nextPalletId = null,
}, ref) {
  // First define currentPallets before any hooks use it
  const currentTruck = trucks[activeTruckIndex] || { name: "Default Vehicle", vehicleType: "trailer", pallets: [] };
  // Ensure we always have a safe array for current pallets referenced throughout the component
  const currentPallets = Array.isArray(currentTruck.pallets) ? currentTruck.pallets : [];
  // For UI lists (side panels, popovers), hide backend visual placeholders
  const currentPalletsVisible = useMemo(() => (
    (currentPallets || []).filter(p => !(p?.meta && p.meta.__isVirtual === true))
  ), [currentPallets]);

  const [settingsOpen, setSettingsOpen] = useState(null); // { source: 'truck'|'unplaced', index: number } | null
  const settingsRef = useRef(null);
  // Refs for functions defined later but exposed via controls
  const importStackedDatasetRef = useRef(null);
  const runStackedV2PlacementRef = useRef(null);
  // Draft of settings being edited; only saved when user confirms
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [newTruckName, setNewTruckName] = useState("");
  const [renamingTruck, setRenamingTruck] = useState(null);
  const [renamingLineId, setRenamingLineId] = useState(null);
  const [newLineName, setNewLineName] = useState("");
  const [infoPallet, setInfoPallet] = useState(null);
  const [cameraActive, setCameraActive] = useState(true);
  // Detect mobile to tune performance
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  const [selectedPalletId, setSelectedPalletId] = useState(null);
  // Notify parent when selection changes
  useEffect(() => {
    if (typeof onSelectedPalletChange === 'function') {
      onSelectedPalletChange(selectedPalletId);
    }
  }, [selectedPalletId, onSelectedPalletChange]);
  const [rowWeightsDisplay, setRowWeightsDisplay] = useState({});
  const [infoPalletIndex, setInfoPalletIndex] = useState(null);
  const [infoSource, setInfoSource] = useState(null); // 'truck' or 'unplaced'
  const [newVehicleType, setNewVehicleType] = useState('trailer');
  // Drivers and combinations (truck+trailer+driver)
  const [drivers, setDrivers] = useState([]); // {id,name,certs: string[]}
  const [newDriverName, setNewDriverName] = useState("");

  // Simple action toast for user feedback (auto-dismiss)
  const [actionToast, setActionToast] = useState(null);
  useEffect(() => {
    if (!actionToast) return;
    const t = setTimeout(() => setActionToast(null), 2000);
    return () => clearTimeout(t);
  }, [actionToast]);
  const [newDriverCerts, setNewDriverCerts] = useState(""); // comma-separated for now
  const [combinations, setCombinations] = useState([]); // {id,truckId,trailerId,driverId}
  const [comboTruckId, setComboTruckId] = useState("");
  const [comboTrailerId, setComboTrailerId] = useState("");
  const [comboDriverId, setComboDriverId] = useState("");
  const [manifestOpen, setManifestOpen] = useState(false);
  const [manifestText, setManifestText] = useState("");

  // Provide local fallbacks if not passed from parent
  const getDimsForTruck = useCallback((truck) => {
    if (getDimsForTruckProp) return getDimsForTruckProp(truck);
    const vt = (truck?.vehicleType || truck?.type || 'trailer');
    return VEHICLE_DIMENSIONS[vt] || VEHICLE_DIMENSIONS.trailer;
  }, [getDimsForTruckProp]);
  const globalRowWeights = globalRowWeightsProp || {};

  // ---------- KPIs: volume%, total weight, axle loads ----------
  const vehicle = currentTruck.vehicleType || 'trailer';
  const totalWeightKg = useMemo(
    () => Math.round((currentPallets || []).reduce((sum, p) => sum + (p?.weight || 0), 0)),
    [currentPallets]
  );
  const usedVolume = useMemo(
    () => (currentPallets || []).reduce((sum, p) => sum + ((p?.dimensions?.width || 0) * (p?.dimensions?.depth || 0) * (p?.dimensions?.height || 0)), 0),
    [currentPallets]
  );
  const containerVolume = (vehicle === 'truck' ? 8.4 : 12) * 2.45 * 2.6;
  const volumePct = useMemo(() => {
    const pct = containerVolume > 0 ? (usedVolume / containerVolume) * 100 : 0;
    return Math.max(0, Math.min(100, pct));
  }, [usedVolume, containerVolume]);

  // Basic, configurable axle rules (EU defaults). Adjust as needed.
  const rules = useMemo(() => {
    if (vehicle === 'truck') {
      return {
        warnFraction: 0.95,
        // Optional gross for solo rigid truck
        grossMax: 18000,
        tractorFrontMax: 8000,
        tractorFrontMin: 4000,
        tractorRearMax: 11500,
      };
    }
    // trailer (tri-axle group)
    return {
      warnFraction: 0.95,
      grossMax: 27000,
      trailerAxleMax: [9000, 9000, 9000],
      trailerGroupMax: 27000,
    };
  }, [vehicle]);

  // Estimate axle loads from total weight.
  // Note: This is an approximation until backend provides precise axle validation.
  const axleResult = useMemo(() => {
    if (vehicle === 'truck') {
      const front = Math.round(totalWeightKg * 0.35);
      const rear = Math.max(0, totalWeightKg - front);
      return {
        tractor: { front, rear },
        trailer: { axleLoads: [] },
        check: undefined,
      };
    }
    // trailer
    const axCount = Array.isArray(rules.trailerAxleMax) && rules.trailerAxleMax.length > 0 ? rules.trailerAxleMax.length : 3;
    const perAxle = Math.round((totalWeightKg || 0) / Math.max(1, axCount));
    return {
      tractor: { front: 0, rear: 0 },
      trailer: { axleLoads: Array.from({ length: axCount }, () => perAxle) },
      check: undefined,
    };
  }, [vehicle, totalWeightKg, rules]);

  // ADD THIS useEffect
  useEffect(() => {
    if (trucks && trucks.length > 0 && activeTruckIndex >= 0 && activeTruckIndex < trucks.length) {
      console.log(
        `useEffect[trucks, activeTruckIndex] - Truck ${activeTruckIndex} pallets:`,
        JSON.parse(JSON.stringify(trucks[activeTruckIndex].pallets))
      );
    } else {
      console.log(
        "useEffect[trucks, activeTruckIndex] - trucks array or activeTruckIndex is not in a valid state to log pallets.",
        { numTrucks: trucks ? trucks.length : 'undefined', activeTruckIndex }
      );
    }
  }, [trucks, activeTruckIndex]);

  // Now these callbacks can safely use currentPallets
  const setInfo = useCallback(
    (pallet, index, source) => {
      setInfoPallet(pallet);
      setInfoPalletIndex(index);
      setInfoSource(source);
    },
    [setInfoPallet, setInfoPalletIndex, setInfoSource]
  );

  const goToPreviousPallet = useCallback(() => {
    // Get the right list based on source
    const palletList = infoSource === 'truck' ? currentPalletsVisible : unplacedPallets;
    
    if (palletList.length === 0) return;
    
    const newIndex = infoPalletIndex === 0 
      ? palletList.length - 1 
      : infoPalletIndex - 1;
      
    setInfo(palletList[newIndex], newIndex, infoSource);
  }, [currentPallets, unplacedPallets, infoPalletIndex, infoSource, setInfo]);

  const goToNextPallet = useCallback(() => {
    // Get the right list based on source
    const palletList = infoSource === 'truck' ? currentPalletsVisible : unplacedPallets;
    
    if (palletList.length === 0) return;
    
    const newIndex = infoPalletIndex === palletList.length - 1 
      ? 0 
      : infoPalletIndex + 1;
      
    setInfo(palletList[newIndex], newIndex, infoSource);
  }, [currentPallets, unplacedPallets, infoPalletIndex, infoSource, setInfo]);

  // Pallet creation fields
  const [palletName, setPalletName] = useState("");
  const [palletWidth, setPalletWidth] = useState(0.8);
  const [palletDepth, setPalletDepth] = useState(1.2);
  const [palletHeight, setPalletHeight] = useState(0.5);
  const [palletWeight, setPalletWeight] = useState(100);
  const [selectedAttribute, setSelectedAttribute] = useState("none");
  const [notToploadable, setNotToploadable] = useState(false);

  // Track whether we only show dangerous pallets
  const [showDangerousOnly, setShowDangerousOnly] = useState(false);
  
  // Additional missing state variables
  const [highlightedPallet, setHighlightedPallet] = useState(null);
  const [enableGrid, setEnableGrid] = useState(true);
  const [smartSnapEnabled, setSmartSnapEnabled] = useState(false);
  const [error, setError] = useState("");
  const [isPacking, setIsPacking] = useState(false);
  // Loading task to tailor the message under the spinner
  const [loadingTask, setLoadingTask] = useState(null); // 'auto' | null (removed greedy & manual rows buttons)
  const [lastAlgorithm, setLastAlgorithm] = useState(null); // stores last algorithm label (e.g., greedy_v2 or full_solver)
  const loadingMessage = useMemo(() => {
    switch (loadingTask) {
      case 'auto':
        return 'Optimizing layout...';
      case 'greedy':
        return 'Optimizing layout (greedy)...';
      case 'rows':
        return 'Calculating row weights...';
      default:
        return 'Loading...';
    }
  }, [loadingTask]);
  // Settings popover state for pallet editing
  // (duplicate declaration removed)
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!settingsOpen) return;
      const el = settingsRef.current;
      if (el && !el.contains(e.target)) setSettingsOpen(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [settingsOpen]);

  // Initialize draft when opening settings; clear when closing
  useEffect(() => {
    if (!settingsOpen) {
      setSettingsDraft(null);
      return;
    }
    const isTruck = settingsOpen.source === 'truck';
    const idx = settingsOpen.index;
    const list = isTruck ? currentPalletsVisible : unplacedPallets;
    const pal = list[idx];
    if (!pal) {
      setSettingsDraft(null);
      return;
    }
    setSettingsDraft({
      name: pal.name || '',
      weight: pal.weight || 0,
      dimensions: { ...pal.dimensions },
      attribute: pal.attribute || 'none',
      notToploadable: !!pal.notToploadable,
    });
  }, [settingsOpen, trucks, activeTruckIndex, unplacedPallets]);

  // Generic updaters moved above saveSettings to avoid TDZ issues
  const updateLoadedPallet = useCallback((index, patch) => {
    setTrucks((prev) => {
      const updated = [...prev];
      const truck = updated[activeTruckIndex];
      if (!truck || !Array.isArray(truck.pallets) || index < 0 || index >= truck.pallets.length) return prev;
      const pallets = [...truck.pallets];
      pallets[index] = { ...pallets[index], ...patch };
      updated[activeTruckIndex] = { ...truck, pallets };
      return updated;
    });
  }, [setTrucks, activeTruckIndex]);

  const updateUnplacedPallet = useCallback((index, patch) => {
    setUnplacedPallets((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }, [setUnplacedPallets]);

  const saveSettings = useCallback(() => {
    if (!settingsOpen || !settingsDraft) return;
    const isTruck = settingsOpen.source === 'truck';
    const index = settingsOpen.index;
    const patch = settingsDraft;
    if (isTruck) {
      updateLoadedPallet(index, patch);
    } else {
      updateUnplacedPallet(index, patch);
    }
    setSettingsOpen(null);
  }, [settingsOpen, settingsDraft, updateLoadedPallet, updateUnplacedPallet]);

  // Driver helpers
  const addDriver = useCallback(() => {
    const name = (newDriverName || "").trim();
    if (!name) return;
    const certs = (newDriverCerts || "").split(",").map(s => s.trim()).filter(Boolean);
    setDrivers(prev => [...prev, { id: crypto.randomUUID(), name, certs }]);
    setNewDriverName("");
    setNewDriverCerts("");
  }, [newDriverName, newDriverCerts]);

  const removeDriver = useCallback((id) => {
    setDrivers(prev => prev.filter(d => d.id !== id));
    // remove from combos
    setCombinations(prev => prev.map(c => c.driverId === id ? { ...c, driverId: "" } : c));
  }, []);

  // Combination helpers
  const addCombination = useCallback(() => {
    if (!comboTruckId || !comboTrailerId) return;
    // prevent duplicates (same truck or trailer already in a combo)
    const collision = combinations.some(c => c.truckId === comboTruckId || c.trailerId === comboTrailerId);
    if (collision) return;
    setCombinations(prev => [...prev, { id: crypto.randomUUID(), truckId: comboTruckId, trailerId: comboTrailerId, driverId: comboDriverId || "" }]);
    setComboTruckId("");
    setComboTrailerId("");
    setComboDriverId("");
  }, [comboTruckId, comboTrailerId, comboDriverId, combinations]);

  const removeCombination = useCallback((id) => {
    setCombinations(prev => prev.filter(c => c.id !== id));
  }, []);

  // Manifest generator (single active vehicle only)
  // Ensure we reference a valid dimension getter inside manifest builder
  const resolvedGetDimsForTruck = getDimsForTruck; // alias for clarity/future extension
  const buildManifest = useCallback(() => {
    const truck = trucks && activeTruckIndex >= 0 && activeTruckIndex < trucks.length
      ? trucks[activeTruckIndex]
      : null;
    if (!truck) {
      return {
        generatedAt: new Date().toISOString(),
        vehicleName: 'No Active Vehicle',
        pallets: [],
        totals: { palletCount: 0, totalWeight: 0, usedVolumeM3: 0, capacityM3: 0 },
      };
    }
  const dims = resolvedGetDimsForTruck(truck);
    const capacityM3 = dims.width * dims.depth * dims.height;
    const pallets = (truck.pallets || []).map(p => ({
      id: p.id,
      name: (p.name || `Pallet ${p.id}`),
      weight: p.weight,
      dims: p.dimensions,
      attr: p.attribute,
      notToploadable: p.notToploadable,
      position: Array.isArray(p.position) ? p.position : undefined,
  // Provide x_m for PDF axle statics (center-of-mass along length). Existing position[0] is x.
  x_m: Array.isArray(p.position) ? p.position[0] : (p.x ?? p.x_m),
      rotation: p.rotation,
    }));
    const totalWeight = pallets.reduce((s,p)=>s+(p.weight||0),0);
    const usedVolumeM3 = pallets.reduce((s,p)=>{
      if (p.dims) return s + (p.dims.width * p.dims.depth * p.dims.height);
      return s;
    },0);
    return {
      generatedAt: new Date().toISOString(),
      vehicleId: truck.id,
      vehicleName: truck.name || `Vehicle ${truck.id}`,
      vehicleType: truck.vehicleType || truck.type || 'trailer',
  truck: { name: truck.name || `Vehicle ${truck.id}` },
      dimensionsM: { width: dims.width, depth: dims.depth, height: dims.height },
      pallets,
      rowWeights: globalRowWeights[truck.id] || {},
      totals: {
        palletCount: pallets.length,
        totalWeight,
        usedVolumeM3: parseFloat(usedVolumeM3.toFixed(3)),
        capacityM3: parseFloat(capacityM3.toFixed(3)),
        volumeUtilisationPct: capacityM3 > 0 ? parseFloat(((usedVolumeM3 / capacityM3) * 100).toFixed(1)) : 0,
      },
      axles: {
        frontKg: axleResult?.tractor?.front ?? null,
        rearKg: axleResult?.tractor?.rear ?? null,
      },
      // Trailer multi-axle loads (if vehicle is a trailer). These are approximate currently.
      trailerAxleLoadsKg: (truck.vehicleType || truck.type || 'trailer') === 'trailer'
        ? (Array.isArray(axleResult?.trailer?.axleLoads) ? axleResult.trailer.axleLoads : [])
        : [],
      trailerAxleMaxKg: (truck.vehicleType || truck.type || 'trailer') === 'trailer'
        ? (Array.isArray(rules?.trailerAxleMax) ? rules.trailerAxleMax : [])
        : [],
      _exportVehicleName: truck.name || `Vehicle-${truck.id}`,
    };
  }, [trucks, activeTruckIndex, getDimsForTruck, globalRowWeights, axleResult, rules]);

  const openManifest = useCallback(() => {
    const data = buildManifest();
    setManifestText(JSON.stringify(data, null, 2));
    setManifestOpen(true);
  }, [buildManifest]);

  const downloadManifest = useCallback(() => {
    const blob = new Blob([manifestText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `loading-manifest-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [manifestText]);

  // Direct PDF export for current active vehicle
  const exportManifestPdf = React.useCallback(async () => {
    try {
      const data = buildManifest();
      // Ensure export name for file naming
      if (data && !data._exportVehicleName) {
        try {
          if (data.vehicleName) data._exportVehicleName = data.vehicleName;
          else if (currentTruck?.name) data._exportVehicleName = currentTruck.name;
        } catch {}
      }
      await generateManifestPdf(data || {});
    } catch (err) {
      alert('PDF export failed: ' + (err?.message || String(err)));
    }
  }, [buildManifest, currentTruck]);
  // Persistent hazard adjacency warnings across the current truck
  const hazardWarnings = useMemo(() => {
    const warnings = [];
    const pallets = currentPallets || [];
    for (let i = 0; i < pallets.length; i++) {
      const a = pallets[i];
      if (!a || !a.position || !a.dimensions) continue;
      const [ax, ay, az] = a.position;
      const { width: wA, depth: dA, height: hA } = getEffectiveDims(a.dimensions, a.rotation || 0);
      const topA = ay + hA / 2;
      const bottomA = ay - hA / 2;
      const attrA = (a.attribute || 'none').toLowerCase();
      for (let j = i + 1; j < pallets.length; j++) {
        const b = pallets[j];
        if (!b || !b.position || !b.dimensions) continue;
        const [bx, by, bz] = b.position;
        const { width: wB, depth: dB, height: hB } = getEffectiveDims(b.dimensions, b.rotation || 0);
        const topB = by + hB / 2;
        const bottomB = by - hB / 2;
        const attrB = (b.attribute || 'none').toLowerCase();
        const TOL = 0.01;
        const sameBand = (topA > bottomB + TOL && bottomA < topB - TOL);
        const conflict = (attrA === 'dangerous' && (attrB === 'food' || attrB === 'medicine')) ||
                         (attrB === 'dangerous' && (attrA === 'food' || attrA === 'medicine'));
        let msg = null;
        if (conflict && sameBand && boxesAdjacentXZ(ax, az, wA, dA, bx, bz, wB, dB, 0.05)) {
          const nameA = a.name || a.id || 'Pallet A';
          const nameB = b.name || b.id || 'Pallet B';
          if (attrA === 'dangerous') msg = `Warning: dangerous goods (${nameA}) touching ${attrB} (${nameB})`;
          else if (attrB === 'dangerous') msg = `Warning: ${attrA} (${nameA}) touching dangerous goods (${nameB})`;
          else msg = `Warning: hazardous adjacency between ${nameA} and ${nameB}`;
        }
        if (boxesOverlapXZStrict(ax, az, wA, dA, bx, bz, wB, dB)) {
          const STACK_GAP = 0.05;
          const aAboveB = Math.abs(bottomA - topB) <= STACK_GAP && bottomA >= topB - STACK_GAP;
          const bAboveA = Math.abs(bottomB - topA) <= STACK_GAP && bottomB >= topA - STACK_GAP;
          if (!msg && conflict && (aAboveB || bAboveA)) {
            const nameA = a.name || a.id || 'Pallet A';
            const nameB = b.name || b.id || 'Pallet B';
            if (attrA === 'dangerous') msg = `Warning: dangerous goods (${nameA}) stacked with ${attrB} (${nameB})`;
            else if (attrB === 'dangerous') msg = `Warning: ${attrA} (${nameA}) stacked with dangerous goods (${nameB})`;
            else msg = `Warning: hazardous stacking between ${nameA} and ${nameB}`;
          }
          const HEAVY_LIMIT = 500;
          if (!msg && aAboveB && (a.weight || 0) > HEAVY_LIMIT) {
            const baseName = b.name || b.id || 'Base Pallet';
            msg = `Warning: heavy pallet (${a.weight}kg) stacked on ${baseName}`;
          } else if (!msg && bAboveA && (b.weight || 0) > HEAVY_LIMIT) {
            const baseName = a.name || a.id || 'Base Pallet';
            msg = `Warning: heavy pallet (${b.weight}kg) stacked on ${baseName}`;
          }
        }
        if (msg) warnings.push({ i, j, message: msg });
      }
    }
    return warnings;
  }, [currentPallets]);

  // Remember dismissal per unique set of warnings (by pallet index pairs)
  const hazardKey = useMemo(() => (
    hazardWarnings.length
      ? JSON.stringify(hazardWarnings.map(w => [w.i, w.j]).sort())
      : null
  ), [hazardWarnings]);
  const [dismissedHazardKey, setDismissedHazardKey] = useState(null);
  const showHazardBanner = hazardWarnings.length > 0 && hazardKey !== dismissedHazardKey;

  // Drag & Drop state for bringing unplaced pallets into the 3D scene
  const [dndActive, setDndActive] = useState(false);
  const [dndPalletId, setDndPalletId] = useState(null);
  const [dndScreenPos, setDndScreenPos] = useState(null); // {x,y}
  const [dndSnappedWorld, setDndSnappedWorld] = useState(null); // [x,y,z]
  const errorTimerRef = useRef(null);

  const showMoveError = useCallback((message) => {
    if (!message) return;
    setError(message);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setError(""), 1600);
  }, []);

  // Derive boundary warning flag from current error text
  const isBoundaryWarning = useMemo(() => /Out of bounds/i.test(error || ""), [error]);

  // Define missing functions
  const removeUnplacedPallet = (index) => {
    setUnplacedPallets(prev => prev.filter((_, idx) => idx !== index));
  };

  const setCurrentPallets = (newPallets) => {
    setTrucks(prev => {
      const updated = [...prev];
      updated[activeTruckIndex] = { ...updated[activeTruckIndex], pallets: newPallets };
      return updated;
    });
  };
  // -------- Imperative controls for external UI (DeviasKit) --------
  const createPalletFrom = useCallback((data) => {
    if (!data || !setUnplacedPallets) return;
    const {
      name,
      width = 0.8,
      depth = 1.2,
      height = 0.5,
      weight = 100,
      attribute = 'none',
      notToploadable = false,
      quantity = 1,
    } = data;

    // Auto-name if missing: "<attribute> <n>" where n is next count across unplaced + all trucks in current line
    const trimmed = (name ?? '').toString().trim();
    const attrKey = (attribute ?? 'none').toString().toLowerCase();
    let finalName = trimmed;
    if (!finalName) {
      const unplacedCount = (unplacedPallets || []).filter(p => (p?.attribute || 'none').toLowerCase() === attrKey).length;
      const loadedCount = (trucks || []).reduce((sum, t) => sum + ((t?.pallets || []).filter(p => (p?.attribute || 'none').toLowerCase() === attrKey).length), 0);
      const nextIdx = unplacedCount + loadedCount + 1;
      finalName = `${attrKey} ${nextIdx}`;
    }

    const qty = Math.max(1, Math.min(500, parseInt(quantity, 10) || 1));
    const nextPallets = [];
    for (let i = 0; i < qty; i++) {
      // Increment name for multiples when auto-named or when user provides a base name
      const numberedName = qty > 1
        ? `${finalName.replace(/\s*\(\d+\)$/,'')} (${i+1})`
        : finalName;
      nextPallets.push({
        id: crypto.randomUUID(),
        name: numberedName,
        dimensions: { width, depth, height },
        weight,
        attribute,
        notToploadable,
        locked: false,
      });
    }
    setUnplacedPallets(prev => [...prev, ...nextPallets]);
  }, [setUnplacedPallets, unplacedPallets, trucks]);

  // Add multiple pallets from Pallet Scanner (already formatted)
  const addPalletsFromScanner = useCallback((scannerPallets) => {
    if (!scannerPallets || !Array.isArray(scannerPallets) || !setUnplacedPallets) return;
    
    const newPallets = scannerPallets.map(p => ({
      id: p.id || crypto.randomUUID(),
      name: p.name || 'Scanned Pallet',
      dimensions: {
        width: p.width || 0.8,
        depth: p.depth || 1.2,
        height: p.height || 1.0,
      },
      weight: p.weight || 300,
      attribute: p.attribute || 'none',
      notToploadable: p.notToploadable || false,
      locked: false,
      // Preserve scanner metadata
      destination: p.destination,
      shipment_id: p.shipment_id,
      sender: p.sender,
      receiver: p.receiver,
      sourceType: 'scanner',
      // Preserve image URLs from scanner
      imageUrl: p.imageUrl || p.image_url || null,
      labelImageUrl: p.labelImageUrl || p.label_image_url || null,
    }));
    
    setUnplacedPallets(prev => [...prev, ...newPallets]);
    setActionToast(`Added ${newPallets.length} pallet${newPallets.length !== 1 ? 's' : ''} from scanner`);
  }, [setUnplacedPallets]);

  const importFromFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      const [, ...dataRows] = rows;
      const validAttributes = { 'no attribute':'none','none':'none','medicine':'medicine','food':'food','dangerous':'dangerous' };

      // Seed per-attribute counters from current scene state
      const seedCounts = new Map();
      const inc = (k, by=1) => seedCounts.set(k, (seedCounts.get(k) || 0) + by);
      (unplacedPallets || []).forEach(p => inc((p?.attribute || 'none').toLowerCase()));
      (trucks || []).forEach(t => (t?.pallets || []).forEach(p => inc((p?.attribute || 'none').toLowerCase())));

      const imported = dataRows.map((row, idx) => {
        const attrRaw = (row[5]?.toString().trim().toLowerCase()) || 'none';
        const attribute = validAttributes[attrRaw] || 'none';
        const attrKey = attribute.toLowerCase();
        const width = parseFloat(row[1]) || 0.8;
        const depth = parseFloat(row[2]) || 1.2;
        const height = parseFloat(row[3]) || 0.5;
        const weight = parseFloat(row[4]) || 100;
        let providedName = (row[0] ?? '').toString().trim();
        if (!providedName) {
          const nextIdx = (seedCounts.get(attrKey) || 0) + 1;
          providedName = `${attrKey} ${nextIdx}`;
          inc(attrKey);
        }
        return {
          id: crypto.randomUUID(),
          name: providedName,
          dimensions: { width, depth, height },
          weight,
          attribute,
          notToploadable: !!row[6],
          locked: false,
        };
      });
      setUnplacedPallets(prev => [...prev, ...imported]);
    };
    reader.readAsArrayBuffer(file);
  }, [setUnplacedPallets, unplacedPallets, trucks]);

  const addVehicle = useCallback((type = 'trailer') => {
    const newTruck = {
      id: crypto.randomUUID(),
      name: `${type === 'truck' ? 'Truck' : 'Trailer'} ${(trucks?.length || 0) + 1}`,
      vehicleType: type,
      pallets: []
    };
    setTrucks(prev => [...prev, newTruck]);
    setActiveTruckIndex((trucks?.length || 0));
  }, [setTrucks, setActiveTruckIndex, trucks]);

  const setVehicleTypeForActive = useCallback((type = 'trailer') => {
    setTrucks(prev => prev.map((t, i) => i === activeTruckIndex ? { ...t, vehicleType: type } : t));
  }, [setTrucks, activeTruckIndex]);

  const renameActiveVehicle = useCallback((name) => {
    if (!name) return;
    setTrucks(prev => prev.map((t, i) => i === activeTruckIndex ? { ...t, name } : t));
  }, [setTrucks, activeTruckIndex]);

  // Lock all pallets that currently have a valid position in the active vehicle.
  // Also stamp unit_id to the current vehicle id to aid downstream logic.
  const lockAllInTruck = useCallback(() => {
    let changed = 0;
    setTrucks((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updated = [...prev];
      const current = updated[activeTruckIndex];
      if (!current) return prev;
      const vehicleId = current.id || 1;
      updated[activeTruckIndex] = {
        ...current,
        pallets: (current.pallets || []).map((p) => {
          const hasPos = Array.isArray(p?.position) && p.position.length === 3;
          if (!hasPos) return p; // only lock placed pallets
          if (!p.locked) changed += 1;
          return { ...p, locked: true, unit_id: p.unit_id || vehicleId };
        }),
      };
      return updated;
    });
    setActionToast(changed > 0 ? `Locked ${changed} pallet${changed === 1 ? '' : 's'}` : 'No placed pallets to lock');
  }, [activeTruckIndex, setTrucks]);

  const controls = useMemo(() => ({
    // creation/import
    createPalletFrom,
    addPalletsFromScanner,
    importFromFile,
    importStackedDataset: (key) => importStackedDatasetRef.current?.(key),
    // toggles
    setShowDangerousOnly,
    setEnableGrid,
    setSmartSnapEnabled,
    // actions
  runAutoPlacement,
  runStackedV2Placement: () => runStackedV2PlacementRef.current?.(),
    openManifest,
    downloadManifest,
  exportManifestPdf,
    removeAllFromTruck,
    removeAllUnplaced,
    lockAllInTruck,
  // expose current line's unplaced list
  getUnplacedForActiveLine: () => unplacedPallets,
  // dnd helpers
  beginUnplacedDrag,
  endUnplacedDrag,
  placeUnplacedById,
    // vehicles
    addVehicle,
    setActiveTruckIndex,
    setVehicleTypeForActive,
    renameActiveVehicle,
    // simple stats
    getCurrentStats: () => ({
      currentPalletsCount: currentPallets.length,
      volumePct,
      totalWeightKg,
    }),
    // selection helpers for outer UI lists
    selectPalletById: (id) => setSelectedPalletId(id),
    removePalletToUnplacedById: (id) => {
      // find pallet index in active truck
      const idx = (trucks?.[activeTruckIndex]?.pallets || []).findIndex(p => p.id === id);
      if (idx >= 0) removePalletFromTruck(idx);
    },
  }), [
    createPalletFrom,
    addPalletsFromScanner,
    importFromFile,
    setShowDangerousOnly,
    setEnableGrid,
    setSmartSnapEnabled,
    runAutoPlacement,
    openManifest,
    downloadManifest,
  exportManifestPdf,
    removeAllFromTruck,
  removeAllUnplaced,
  lockAllInTruck,
  unplacedPallets,
  beginUnplacedDrag,
  endUnplacedDrag,
  placeUnplacedById,
    addVehicle,
    setActiveTruckIndex,
    setVehicleTypeForActive,
    renameActiveVehicle,
    currentPallets.length,
    volumePct,
    totalWeightKg,
  trucks,
  activeTruckIndex,
  ]);

  React.useImperativeHandle(ref, () => controls, [controls]);

  // Generic updater for a loaded pallet is defined earlier

  // Determine the current truck and its pallets
  const renameTruck = (idx, newName) => {
    setTrucks((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], name: newName };
      return updated;
    });
  };

  // Line management helpers
  const addLine = useCallback(() => {
    if (!setLines) return;
    const nextIndex = (Array.isArray(lines) ? lines.length : 0) + 1;
    const id = crypto.randomUUID();
    const newLine = { id, name: `Line ${nextIndex}`, trucks: [] };
    setLines((prev) => [...(prev || []), newLine]);
    setActiveLineId && setActiveLineId(id);
    setActiveTruckIndex && setActiveTruckIndex(0);
  }, [setLines, lines, setActiveLineId, setActiveTruckIndex]);

  const saveLineRename = useCallback(() => {
    if (!setLines || !renamingLineId) return;
    setLines((prev) => (prev || []).map((l) => l.id === renamingLineId ? { ...l, name: newLineName || l.name } : l));
    setRenamingLineId(null);
    setNewLineName("");
  }, [setLines, renamingLineId, newLineName]);

  const cancelLineRename = useCallback(() => {
    setRenamingLineId(null);
    setNewLineName("");
  }, []);

  // -------------- FILE IMPORT (Excel) --------------
  const handleFileUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Convert to array-of-arrays
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      const [, ...dataRows] = rows;

      const importedPallets = dataRows.map((row, idx) => {
        const name = row[0] || `Imported${idx}`;
        const width = parseFloat(row[1]) || 0.8;
        const depth = parseFloat(row[2]) || 1.2;
        const height = parseFloat(row[3]) || 0.5;
        const weight = parseFloat(row[4]) || 100;
        const validAttributes = {
          "no attribute": "none",
          "none": "none",
          "medicine": "medicine",
          "food": "food",
          "dangerous": "dangerous",
        };
        
        const rawAttribute = row[5] ? row[5].toString().trim().toLowerCase() : "none";
        const attribute = validAttributes[rawAttribute] || "none";
        
        const notToploadable = !!row[6];

        return {
          id: crypto.randomUUID(),
          name,
          dimensions: { width, depth, height },
          weight,
          attribute,
          notToploadable,
          locked: false,
        };
      });

      setUnplacedPallets((prev) => [...prev, ...importedPallets]);
    };
    reader.readAsArrayBuffer(file);
  }, [
    setUnplacedPallets // ← added
  ]);

  const createPallet = useCallback(() => {
    setUnplacedPallets((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: palletName || `Pallet${Date.now()}`,
        dimensions: { width: palletWidth, depth: palletDepth, height: palletHeight },
        weight: palletWeight,
        attribute: selectedAttribute,
        notToploadable,
        locked: false,
      },
    ]);
    setPalletName("");
  }, [
    palletName,
    palletWidth,
    palletDepth,
    palletHeight,
    palletWeight,
    selectedAttribute,
    notToploadable,
    setUnplacedPallets
  ]);

  // Add pallet to the current truck
  const addPalletToTruck = useCallback(
    (pallet, initialPosition = null, preferredRotation = 0) => {
      // Prepare new pallet with optional initial position (from drop) or default near the back
      let startPos;
      if (Array.isArray(initialPosition) && initialPosition.length === 3) {
        startPos = initialPosition;
      } else {
  // Compute effective dimensions (respect potential rotation at creation)
  const eff = getEffectiveDims(pallet.dimensions, preferredRotation || 0);
  const vehicleType = currentTruck.vehicleType || 'trailer';
  const halfD = VEHICLE_DIMENSIONS[vehicleType].depth / 2;
  const halfW = VEHICLE_DIMENSIONS[vehicleType].width / 2;
  const backZ = halfD - eff.depth / 2; // near the back door
  // Start flush to the LEFT wall to avoid initial gap (can be moved later)
  const leftX = -halfW + eff.width / 2;
  startPos = [leftX, eff.height / 2, backZ];
      }
      const newPallet = {
        ...pallet,
        rotation: preferredRotation || 0,
        position: startPos,
        unit_id: currentTruck.id || 1
      };

      // Snap to nearby edges/piles
      const snapped = edgeSnapToPallets(
        newPallet.position,
        newPallet,
        currentPallets,
        currentPallets.length,
        currentTruck.vehicleType || 'trailer',
            smartSnapEnabled,
            startPos[1],
            true
      );
      if (snapped) newPallet.position = snapped;

      // 2) Ensure there’s at least one truck
      setTrucks((prev) => {
        const base =
          prev.length === 0
            ? [{ id: crypto.randomUUID(), name: "Truck 1", pallets: [] }]
            : prev;

        // Safely insert into the active truck
        return base.map((truck, idx) =>
          idx === activeTruckIndex
            ? { ...truck, pallets: [...truck.pallets, newPallet] }
            : truck
        );
      });

      // If this was the first truck, reset truck selection
      if (trucks.length === 0) {
        setActiveTruckIndex(0);
      }

      // 3) Delay removal from unplaced
      setTimeout(() => {
        setUnplacedPallets((prev) => prev.filter((p) => p.id !== pallet.id));
      }, 0);
    },
    [
      activeTruckIndex,
      currentPallets,
      trucks,
      setActiveTruckIndex,
      setTrucks,
      setUnplacedPallets,
      currentTruck.vehicleType,
      smartSnapEnabled
    ]
  );

  // Helper to find unplaced pallet by id
  const getUnplacedById = useCallback((id) => unplacedPallets.find(p => p.id === id) || null, [unplacedPallets]);

  // Imperative helpers so outer UI can start/end a drag and click-to-add
  function beginUnplacedDrag(palletOrId) {
    let pallet = palletOrId;
    if (!pallet || typeof pallet !== 'object') {
      pallet = getUnplacedById(palletOrId);
    }
    if (!pallet) return;
    setDndActive(true);
    setDndPalletId(pallet.id);
  }

  function endUnplacedDrag() {
    setDndActive(false);
    setDndPalletId(null);
    setDndScreenPos(null);
    setDndSnappedWorld(null);
  }

  function placeUnplacedById(id) {
    const pallet = getUnplacedById(id);
    if (!pallet) return false;
    // Use default insertion position and snapping
    addPalletToTruck(pallet, null, 0);
    return true;
  }

  // Drag start on unplaced item
  const onUnplacedDragStart = useCallback((pallet, ev) => {
    ev.dataTransfer.setData('text/plain', pallet.id);
    ev.dataTransfer.effectAllowed = 'copy';
    setDndActive(true);
    setDndPalletId(pallet.id);
  }, []);

  const onUnplacedDragEnd = useCallback(() => {
    setDndActive(false);
    setDndPalletId(null);
    setDndScreenPos(null);
    setDndSnappedWorld(null);
  }, []);

  // Dragging over the 3D area
  const onSceneDragOver = useCallback((e) => {
    if (!dndActive) return;
    e.preventDefault();
    setDndScreenPos({ x: e.clientX, y: e.clientY });
  }, [dndActive]);

  const onSceneDrop = useCallback((e) => {
    if (!dndActive) return;
    e.preventDefault();
    const id = dndPalletId;
    const pallet = id ? getUnplacedById(id) : null;
    if (pallet && dndSnappedWorld) {
      addPalletToTruck(pallet, dndSnappedWorld, 0);
    }
    // clear dnd state
    setDndActive(false);
    setDndPalletId(null);
    setDndScreenPos(null);
    setDndSnappedWorld(null);
  }, [dndActive, dndPalletId, dndSnappedWorld, getUnplacedById, addPalletToTruck]);

  const onSceneDragLeave = useCallback(() => {
    if (!dndActive) return;
    setDndScreenPos(null);
    setDndSnappedWorld(null);
  }, [dndActive]);

  // Canvas-side helper to compute world position/snapping for live preview
  function DropPreview({ screen, pallet }) {
    const { camera, gl } = useThree();
    const [previewPos, setPreviewPos] = useState(null);

    useEffect(() => {
      if (!screen || !pallet) {
        setPreviewPos(null);
        return;
      }
      const rect = gl.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((screen.x - rect.left) / rect.width) * 2 - 1,
        -((screen.y - rect.top) / rect.height) * 2 + 1
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(ndc, camera);
      const ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0); // y=0
      const intersect = new THREE.Vector3();
      const hit = raycaster.ray.intersectPlane(ground, intersect);
      if (!hit) {
        setPreviewPos(null);
        return;
      }
      const vehicle = currentTruck.vehicleType || 'trailer';
      // Candidate pos ignores Y; snapping will choose proper layer
      const candidate = [intersect.x, pallet.dimensions.height / 2, intersect.z];
      let snapped = edgeSnapToPallets(
        candidate,
        { ...pallet, rotation: 0 },
        currentPallets,
        currentPallets.length,
        vehicle,
        smartSnapEnabled,
        null,
        false // preview: avoid wall-snapping to reduce stickiness near boundaries
      );
      // If snapping fails (e.g., out of bounds), clamp to bounds and retry minimal
  if (!snapped && isWithinVehicleBounds(candidate, pallet.dimensions, vehicle, pallet.rotation)) {
        snapped = candidate;
      }
      setPreviewPos(snapped);
      // Expose to outer state for drop commit
      setDndSnappedWorld(snapped || null);
    }, [screen, pallet, camera, gl, currentPallets, currentTruck.vehicleType, smartSnapEnabled]);

    if (!previewPos) return null;
    const rotY = 0;
    return (
      <group position={previewPos} rotation={[0, rotY, 0]}>
        <mesh>
          <boxGeometry args={[pallet.dimensions.width, pallet.dimensions.height, pallet.dimensions.depth]} />
          <meshStandardMaterial color="#3B82F6" transparent opacity={0.35} />
        </mesh>
        <lineSegments geometry={new THREE.EdgesGeometry(new THREE.BoxGeometry(pallet.dimensions.width, pallet.dimensions.height, pallet.dimensions.depth))}>
          <lineBasicMaterial color="#1D4ED8" />
        </lineSegments>
      </group>
    );
  }
  

  const removePalletFromTruck = useCallback(
    (index) => {
      setTrucks((prev) => {
        const updated = [...prev];
        const removed = updated[activeTruckIndex].pallets[index];
        
        // CRITICAL FIX: Clean removed pallet of placement-specific properties
        const cleanedRemoved = {
          id: removed.id,
          name: removed.name,
          dimensions: removed.dimensions,
          weight: removed.weight,
          attribute: removed.attribute,
          notToploadable: removed.notToploadable,
          // Remove position, rotation, locked, and any other placement-specific props
        };
        
        updated[activeTruckIndex] = {
          ...updated[activeTruckIndex],
          pallets: updated[activeTruckIndex].pallets.filter((_, i) => i !== index),
        };
        // Prevent duplicates in unplaced:
        setUnplacedPallets((prevUnplaced) =>
          prevUnplaced.some((p) => p.id === removed.id)
            ? prevUnplaced
            : [...prevUnplaced, cleanedRemoved]
        );
        return updated;
      });
    },
    [activeTruckIndex, setUnplacedPallets]
  );

  function removeAllFromTruck() {
    setTrucks((prev) => {
      const updated = [...prev];
      const removedPallets = (updated[activeTruckIndex]?.pallets) || [];
      const cleanedRemovedPallets = removedPallets.map(p => ({
        id: p.id,
        name: p.name,
        dimensions: p.dimensions,
        weight: p.weight,
        attribute: p.attribute,
        notToploadable: p.notToploadable,
      }));
      setUnplacedPallets((prevUnplaced) => {
        const existingIds = new Set(prevUnplaced.map((p) => p.id));
        const toAdd = cleanedRemovedPallets.filter((p) => p && !existingIds.has(p.id));
        return [...prevUnplaced, ...toAdd];
      });
      updated[activeTruckIndex] = { ...updated[activeTruckIndex], pallets: [] };
      return updated;
    });
    setSelectedPalletId(null);
  }

  // Remove *all* unplaced pallets
  function removeAllUnplaced() {
    setUnplacedPallets(() => []);
  }

  // …  

  async function runPlacementInternal(options = {}) {
    const {
      solverVersion = null,
      label = solverVersion ? 'stacked_v2' : 'optimizer',
      toastPrefix = solverVersion ? 'Stacked v2 placed' : 'Placed',
      loadingKey = solverVersion ? 'stacked_v2' : 'auto'
    } = options;
    // Check if activeTruckIndex is valid for the current trucks prop
    if (activeTruckIndex < 0 || activeTruckIndex >= trucks.length || !trucks[activeTruckIndex]) {
      console.error(
        "runAutoPlacement: No valid truck at activeTruckIndex or trucks array is not updated yet.",
        { activeTruckIndex, numTrucks: trucks.length }
      );
      alert("Please ensure a vehicle is selected and fully added before auto-placing.");
      return;
    }
    // Now we know trucks[activeTruckIndex] is a valid truck object
    const currentTruckInstance = trucks[activeTruckIndex];

    if (unplacedPallets.length === 0 && !currentTruckInstance.pallets.some(p => p.locked)) { // also consider locked pallets already in truck
      alert("No unplaced pallets to arrange, and no locked pallets in the truck to consider.");
      return;
    }

  // Start loading state
  setLoadingTask(loadingKey);
  setIsPacking(true);

    try {
      const vehicleDimensions = VEHICLE_DIMENSIONS[currentTruckInstance.vehicleType || 'trailer'];
      
      // ✅ Add unit_id to unplaced pallets (they will be placed in the current vehicle)
      // Create clean copies for solver - avoid any frontend contamination
      const createCleanPalletForSolver = (pallet, unitId) => {
        const clean = {
          // Core required fields
          id: pallet.id,
          name: pallet.name || `Pallet${pallet.id}`,
          dimensions: {
            width: pallet.dimensions.width,
            depth: pallet.dimensions.depth,
            height: pallet.dimensions.height
          },
          weight: pallet.weight || 0,
          
          // Optional fields
          attribute: pallet.attribute || 'none',
          notToploadable: Boolean(pallet.notToploadable),
          locked: Boolean(pallet.locked),
          unit_id: unitId,
          
          // Position only if locked (for existing placed pallets)
          ...(pallet.locked && pallet.position ? {
            position: [...pallet.position], // Clean copy of position array
            rotation: pallet.rotation || 0
          } : {})
        };
        return clean;
      };

      const palletsCopy = unplacedPallets.map(p => 
        createCleanPalletForSolver({...p, locked: false}, currentTruckInstance.id || 1)
      );
      
      // ✅ Preserve unit_id for locked pallets already in truck
      const truckPallets = (currentTruckInstance.pallets || [])
        .filter(p => p.locked)
        .map(p => createCleanPalletForSolver(p, p.unit_id || currentTruckInstance.id || 1));
      
      const allToSolve = [...palletsCopy, ...truckPallets];

      if (allToSolve.length === 0) {
        alert("No pallets available for auto-placement (neither unplaced nor locked in truck).");
        setIsPacking(false);
        return;
      }

  // Use authenticated API service so Authorization header is included
  const { apiService } = await import('./services/apiService');
  const response = await apiService.request('/arrange', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pallets: allToSolve,
          truckDimensions: vehicleDimensions,
          vehicleId: currentTruckInstance.id,
          resetRows: true,
          ...(solverVersion ? { solverVersion } : {})
        }),
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

  const result = await response.json();
      
      console.log("Auto-placement result (from backend):", JSON.parse(JSON.stringify(result))); // Log a deep copy

      // Ensure result is defined before trying to access its properties
      if (!result || !result.arrangement) {
        console.error("Auto-placement: Backend result or arrangement is missing.", result);
        alert("Auto-placement failed: Invalid data from server.");
        setIsPacking(false);
        return;
      }

      // Update only this specific truck's pallets
      setTrucks(prevTrucks => {
        // Ensure prevTrucks is a valid array; default to empty if not.
        const currentPrevTrucks = Array.isArray(prevTrucks) ? prevTrucks : [];
        console.log("runAutoPlacement/setTrucks - prevTrucks (normalized):", JSON.parse(JSON.stringify(currentPrevTrucks)));
        console.log("runAutoPlacement/setTrucks - activeTruckIndex:", activeTruckIndex);

        // Validate activeTruckIndex
        if (activeTruckIndex < 0 || activeTruckIndex >= currentPrevTrucks.length) {
          if (currentPrevTrucks.length === 0 && activeTruckIndex === 0) {
            // This case might be okay if a new truck was just added and prevTrucks is not yet updated
            // but it's generally safer to ensure the truck exists in prevTrucks.
            // For now, let's log and proceed cautiously or return prevTrucks.
            console.warn("runAutoPlacement/setTrucks - activeTruckIndex is 0, but prevTrucks is empty. This might be a timing issue if a truck was just added.");
            // If you expect a truck to be there, this is an error.
            // To be safe, if the truck doesn't exist in prevTrucks, don't try to update it.
            // However, if a new truck was added and this is the first auto-placement,
            // prevTrucks might not yet reflect it if setTrucks from addTruck and this runAutoPlacement are too close.
            // For this specific problem, we assume a truck *should* exist at activeTruckIndex.
          } else {
            console.error("runAutoPlacement/setTrucks - activeTruckIndex is out of bounds.", { activeTruckIndex, numTrucks: currentPrevTrucks.length });
            return currentPrevTrucks; // Return previous state if index is invalid
          }
        }
        
        // Prepare the new pallet data for the active truck
        const srcArrangement = Array.isArray(result.arrangement) ? result.arrangement : [];
        const isVirtualPlaced = (p) => p?.isPlaceholder === true || p?.virtualPadding === true || (typeof p?.id === 'string' && p.id.startsWith('pad_'));
        // First pass: normalize and preserve unit_id/fields
        let palletsToSetForActiveTruck = srcArrangement.map(p => ({
          id: p.id ?? crypto.randomUUID(),
          name: p.name || "Unnamed Pallet",
          dimensions: p.dimensions || { width: 0.8, depth: 1.2, height: 0.5 },
          weight: p.weight || 0,
          attribute: p.attribute || "none",
          notToploadable: !!p.notToploadable,
          locked: !!p.locked,
          fused: !!p.fused,
          components: Array.isArray(p.components) ? p.components : undefined,
          position: Array.isArray(p.position) && p.position.length === 3 ? p.position : [0, 0, 0],
          rotation: typeof p.rotation === 'number' ? p.rotation : 0,
          unit_id: p.unit_id || currentTruckInstance.id || 1,
          // Preserve image URLs through solver
          imageUrl: p.imageUrl || p.image_url,
          labelImageUrl: p.labelImageUrl || p.label_image_url,
          // Preserve metadata
          destination: p.destination,
          shipment_id: p.shipment_id,
          sender: p.sender,
          receiver: p.receiver,
          // Mark as auto-placed so dashboard can show high stability score once
          meta: { ...(p.meta || {}), __autoPlaced: true, __isVirtual: isVirtualPlaced(p) }
        }));

        // De-duplicate IDs defensively to avoid React key collisions when backend repeats ids
        const seen = new Map(); // id -> count
        palletsToSetForActiveTruck = palletsToSetForActiveTruck.map(item => {
          const baseId = String(item.id);
          const n = (seen.get(baseId) || 0) + 1;
          seen.set(baseId, n);
          if (n === 1) return item;
          // Clone with unique id suffix
          return { ...item, id: `${baseId}__${n}` };
        });
        console.log("runAutoPlacement/setTrucks - palletsToSetForActiveTruck:", JSON.parse(JSON.stringify(palletsToSetForActiveTruck)));

        // Create the new trucks array state, ensuring a truck exists at activeTruckIndex
        // Avoid relying on the outer `trucks` prop here to prevent stale-closure issues.
        let base = currentPrevTrucks.length > 0 ? [...currentPrevTrucks] : [];
        if (base.length === 0) {
          // Initialize with the current truck snapshot if nothing exists yet
          const fallbackTruck = {
            id: currentTruckInstance.id || crypto.randomUUID(),
            name: currentTruckInstance.name || 'Truck 1',
            vehicleType: currentTruckInstance.vehicleType || 'trailer',
            pallets: []
          };
          base = [fallbackTruck];
        }
        // Pad array if needed
        while (base.length <= activeTruckIndex) {
          base.push({ id: crypto.randomUUID(), name: `Truck ${base.length+1}`, pallets: [], vehicleType: currentTruckInstance.vehicleType || 'trailer' });
        }
        // Update the active truck's pallets
        base[activeTruckIndex] = {
          ...base[activeTruckIndex],
          pallets: palletsToSetForActiveTruck
        };
        const newTrucksState = base;

        console.log("runAutoPlacement/setTrucks - newTrucksState to be returned from updater:", JSON.parse(JSON.stringify(newTrucksState)));
        return newTrucksState;
      });

      // Remove placed pallets from unplaced list – include halves inside any fused pair
      const isVirtual = (p) => p?.isPlaceholder === true || p?.virtualPadding === true || (typeof p?.id === 'string' && p.id.startsWith('pad_'));
      const arrangedReal = (Array.isArray(result.arrangement) ? result.arrangement : []).filter(p => !isVirtual(p));
      const placedIds = new Set();
      arrangedReal.forEach(p => {
        if (p?.id) placedIds.add(String(p.id));
        if (p?.fused && Array.isArray(p.components)) {
          p.components.forEach(c => c?.id && placedIds.add(String(c.id)));
        }
      });
      // If backend omitted some ids, try to match by name as a weak fallback
      const placedNames = new Set(arrangedReal.map(p => p?.name).filter(Boolean));
      // Also remove any fused composite parent whose components were individually placed.
      const placedComponentIds = new Set();
      arrangedReal.forEach(p => {
        if (Array.isArray(p?.components)) {
          p.components.forEach(c => c?.id && placedComponentIds.add(String(c.id)));
        }
      });
      setUnplacedPallets(prev => prev.filter(p => {
        const idStr = String(p.id);
        if (placedIds.has(idStr) || (p?.name && placedNames.has(p.name))) return false;
        if (p.fused && Array.isArray(p.components) && p.components.length > 0) {
          const allComponentsPlaced = p.components.every(c => placedComponentIds.has(String(c.id)));
          if (allComponentsPlaced) return false; // drop fused parent; components are placed
        }
        return true;
      }));

      // UX: clear selection and show a quick toast with count
      try { setSelectedPalletId(null); } catch {}
      try {
        const allArr = Array.isArray(result.arrangement) ? result.arrangement : [];
        const isVirtualToast = (p) => p?.isPlaceholder === true || p?.virtualPadding === true || (typeof p?.id === 'string' && p.id.startsWith('pad_'));
        const realCount = allArr.filter(p => !isVirtualToast(p)).length;
        const virtCount = allArr.length - realCount;
        setActionToast(`${toastPrefix} ${realCount} pallet${realCount === 1 ? '' : 's'}${virtCount > 0 ? ` (+${virtCount} placeholders)` : ''}`);
        setTimeout(() => setActionToast(null), 2000);
      } catch {}

      // Update row weights display
      setRowWeightsDisplay(result.rowWeights || {});
      
      // Call callback to update dashboard with row weights
      if (onRowWeightsUpdate && result.rowWeights) {
        onRowWeightsUpdate(result.rowWeights, activeTruckIndex, currentTruckInstance.id);
      }

      setLastAlgorithm(result.algorithm || label);
    } catch (error) {
      console.error("Error during auto-placement:", error);
      alert(`Error during auto-placement: ${error.message}`);
    } finally {
  // End loading state
  setIsPacking(false);
  setLoadingTask(null);
    }
  }

  async function runAutoPlacement() {
    return runPlacementInternal({ label: 'optimizer', toastPrefix: 'Placed' });
  }

  async function runStackedV2Placement() {
    return runPlacementInternal({ solverVersion: 'stacked_v2', label: 'stacked_v2', toastPrefix: 'Stacked v2 placed', loadingKey: 'stacked_v2' });
  }

  // Local stack solver demo importer (menu-based)
  const [demoDatasetKey, setDemoDatasetKey] = useState('full');

  const importStackedDataset = useCallback(async (key) => {
    try {
      setIsPacking(true);
      setLoadingTask('import-demo');
      let payloadModule;
      if (key === 'full') {
        payloadModule = await import('./sample_inputs/stack_solver_full_demo.json');
      } else if (key === 'variant_a') {
        payloadModule = await import('./sample_inputs/stack_solver_variant_a.json');
      } else if (key === 'variant_b') {
        payloadModule = await import('./sample_inputs/stack_solver_variant_b.json');
      } else {
        throw new Error(`Unknown dataset key: ${key}`);
      }
      const payload = payloadModule?.default || payloadModule;
      const palletsFromSample = Array.isArray(payload?.pallets) ? payload.pallets : [];
      if (!palletsFromSample.length) throw new Error('Local demo dataset is empty');
      const vehicleId = payload?.vehicleId || crypto.randomUUID();
      const normalizeBase = (p) => {
        const baseName = p.name || p.id || 'Pallet';
        let finalName = baseName;
        // Only append classification tag if pallet provides one AND name does not already contain a tag.
        if (!/\[C[0-3]\]/.test(baseName) && typeof p.classification === 'string' && /^C[0-3]$/.test(p.classification)) {
          finalName = `${baseName} [${p.classification}]`;
        }
        // Structural class override: prefer explicit stackingClass; else map classification if valid.
        const structuralClass = (typeof p.stackingClass === 'string' && /^C[0-3]$/.test(p.stackingClass))
          ? p.stackingClass
          : (typeof p.classification === 'string' && /^C[0-3]$/.test(p.classification) ? p.classification : undefined);
        return {
          id: p.id || crypto.randomUUID(),
          name: finalName,
          dimensions: p.dimensions || { width: 0.8, depth: 1.2, height: 1.2 },
          weight: p.weight || 0,
          attribute: p.attribute || 'none',
          notToploadable: !!p.notToploadable,
          rotation: typeof p.rotation === 'number' ? p.rotation : 0,
          fused: !!p.fused,
          components: Array.isArray(p.components) ? p.components : undefined,
          unit_id: p.unit_id || vehicleId,
          locked: false,
          position: undefined,
          stackingClass: structuralClass
        };
      };
      const unplacedNormalized = palletsFromSample.map(normalizeBase);
      if (typeof setUnplacedPallets === 'function') setUnplacedPallets(unplacedNormalized);
      setSelectedPalletId(null);
      setActionToast(`Unplaced demo loaded (${key})`);
      setTimeout(() => setActionToast(null), 2200);
    } catch (error) {
      console.error('Failed to import local unplaced demo:', error);
      alert(`Failed to import demo dataset: ${error.message}`);
    } finally {
      setIsPacking(false);
      setLoadingTask(null);
    }
  }, [setIsPacking, setLoadingTask, setUnplacedPallets, setSelectedPalletId, setActionToast]);

  // Backwards-compat alias used by previous button
  const importStackedV2Demo = useCallback(() => importStackedDataset('full'), [importStackedDataset]);

  // Update refs for functions exposed via controls (defined after useMemo)
  useEffect(() => { importStackedDatasetRef.current = importStackedDataset; }, [importStackedDataset]);
  useEffect(() => { runStackedV2PlacementRef.current = runStackedV2Placement; });

  // Greedy placement (v2) using backend flags greedy=true, greedyVersion=2
  async function runGreedyPlacement() {
    if (activeTruckIndex < 0 || activeTruckIndex >= trucks.length || !trucks[activeTruckIndex]) {
      alert("Please select a vehicle before running greedy placement.");
      return;
    }
    const currentTruckInstance = trucks[activeTruckIndex];
    if (unplacedPallets.length === 0 && !currentTruckInstance.pallets.some(p => p.locked)) {
      alert("No unplaced or locked pallets to arrange.");
      return;
    }
    setLoadingTask('greedy');
    setIsPacking(true);
    try {
      const vehicleDimensions = VEHICLE_DIMENSIONS[currentTruckInstance.vehicleType || 'trailer'];
      const normalize = (pallet, unitId) => ({
        id: pallet.id,
        name: pallet.name || pallet.id,
        dimensions: { ...pallet.dimensions },
        weight: pallet.weight || 0,
        attribute: pallet.attribute || 'none',
        notToploadable: !!pallet.notToploadable,
        locked: !!pallet.locked,
        unit_id: unitId,
        ...(pallet.locked && pallet.position ? { position: [...pallet.position], rotation: pallet.rotation || 0 } : {})
      });
      const baseUnitId = currentTruckInstance.id || 1;
      const solverUnplaced = unplacedPallets.map(p => normalize({ ...p, locked: false }, baseUnitId));
      const lockedPlaced = (currentTruckInstance.pallets || []).filter(p => p.locked).map(p => normalize(p, p.unit_id || baseUnitId));
      const allToSolve = [...solverUnplaced, ...lockedPlaced];
      if (allToSolve.length === 0) {
        alert("Nothing to solve for greedy placement.");
        setLoadingTask(null); setIsPacking(false); return;
      }
      const { apiService } = await import('./services/apiService');
      const response = await apiService.request('/arrange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pallets: allToSolve,
          truck: vehicleDimensions,   // new backend expects 'truck'
          truckDimensions: vehicleDimensions, // legacy field retained for compatibility
          vehicleId: currentTruckInstance.id,
          greedy: true,
            greedyVersion: 2,
          timeBudgetSec: 2.5,
          useFallback: false
        })
      });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      const result = await response.json();
      if (!result || !Array.isArray(result.arrangement)) {
        throw new Error('Invalid greedy response');
      }
      // Update truck pallets
      setTrucks(prev => {
        const copy = [...prev];
        if (!copy[activeTruckIndex]) return prev;
        let palletsToSet = result.arrangement.map(p => ({
          id: p.id || crypto.randomUUID(),
          name: p.name || p.id || 'Pallet',
          dimensions: p.dimensions || { width: 0.8, depth: 1.2, height: 0.5 },
          weight: p.weight || 0,
          attribute: p.attribute || 'none',
          notToploadable: !!p.notToploadable,
          locked: false,
          fused: !!p.fused,
          components: Array.isArray(p.components) ? p.components : undefined,
          position: Array.isArray(p.position) ? p.position : [0, (p.dimensions?.height || 0.5)/2, 0],
          rotation: typeof p.rotation === 'number' ? p.rotation : 0,
          unit_id: p.unit_id || currentTruckInstance.id || 1,
          // Preserve image URLs through solver
          imageUrl: p.imageUrl || p.image_url,
          labelImageUrl: p.labelImageUrl || p.label_image_url,
          // Preserve metadata
          destination: p.destination,
          shipment_id: p.shipment_id,
          sender: p.sender,
          receiver: p.receiver,
          meta: { ...(p.meta || {}), __autoPlaced: true }
        }));
        // Deduplicate ids
        const seen = new Map();
        palletsToSet = palletsToSet.map(pl => {
          const base = String(pl.id);
            const n = (seen.get(base) || 0) + 1; seen.set(base, n);
          return n === 1 ? pl : { ...pl, id: `${base}__${n}` };
        });
        copy[activeTruckIndex] = { ...copy[activeTruckIndex], pallets: palletsToSet };
        return copy;
      });
      // Remove placed from unplaced
      const placedIds = new Set(result.arrangement.map(p => String(p.id)).filter(Boolean));
      setUnplacedPallets(prev => prev.filter(p => !placedIds.has(String(p.id))));
      // Row weights
      if (result.rowWeights) {
        setRowWeightsDisplay(result.rowWeights);
        if (onRowWeightsUpdate) onRowWeightsUpdate(result.rowWeights, activeTruckIndex, currentTruckInstance.id);
      }
      // Algorithm feedback
      if (result.algorithm) {
        setLastAlgorithm(result.algorithm);
      } else {
        setLastAlgorithm('greedy');
      }
      const count = result.arrangement.length;
      setActionToast(`Greedy placed ${count} pallet${count === 1 ? '' : 's'}`);
    } catch (err) {
      console.error('Greedy placement error', err);
      alert(`Greedy placement failed: ${err.message}`);
    } finally {
      setIsPacking(false);
      setLoadingTask(null);
    }
  }
  

  const toggleLocked = useCallback((idx) => {
    setTrucks((prev) => {
      const updated = [...prev];
      const palletToToggle = updated[activeTruckIndex].pallets[idx];
      updated[activeTruckIndex].pallets[idx] = {
        ...palletToToggle,
        locked: !palletToToggle.locked,
      };
      return updated;
    });
  }, [activeTruckIndex, setTrucks]);

  const handleDrag = useCallback((index, newPosition, newRotation) => {
    const pallet = currentPallets[index];
    const vehicleType = currentTruck.vehicleType || 'trailer';
    
    // Check if within boundaries
    if (!isWithinVehicleBounds(newPosition, pallet.dimensions, vehicleType, newRotation ?? pallet.rotation)) {
      return;
    }
    // Extra safety: ensure no collision before committing
    const testPallet = { ...pallet, rotation: newRotation ?? pallet.rotation };
    if (!canMoveToPosition(newPosition, testPallet, currentPallets, index, vehicleType)) {
      return;
    }
    
    setTrucks((prevTrucks) => {
      const newTrucks = [...prevTrucks];
      const newPallets = [...newTrucks[activeTruckIndex].pallets];
      newPallets[index] = {
        ...newPallets[index],
        position: newPosition,
        rotation: newRotation,
      };
      newTrucks[activeTruckIndex] = {
        ...newTrucks[activeTruckIndex],
        pallets: newPallets,
      };
      return newTrucks;
    });
  }, [currentPallets, setTrucks, activeTruckIndex, currentTruck.vehicleType]);

  // Add this to your TruckLoadingScene.js or similar file
  function displayPlacementExplanations(explanations) {
    const consoleElement = document.getElementById('placement-console');
    
    if (!consoleElement) {
        console.log("Console element not found");
        return;
    }
    
    // Clear previous content
    consoleElement.innerHTML = '';
    
    // Add header
    const header = document.createElement('h3');
    header.textContent = 'Placement Explanations';
    consoleElement.appendChild(header);
    
    // Add each explanation
    explanations.forEach(exp => {
        const item = document.createElement('div');
        item.className = exp.rejected ? 'rejected-placement' : 'successful-placement';
        
        const palletInfo = document.createElement('div');
        palletInfo.className = 'pallet-info';
        palletInfo.textContent = `Pallet ${exp.pallet}: ${exp.position.type} (${exp.position.x}, ${exp.position.y}, ${exp.position.z})`;
        
        const reasonInfo = document.createElement('div');
        reasonInfo.className = 'reason';
        reasonInfo.textContent = exp.reason;
        
        item.appendChild(palletInfo);
        item.appendChild(reasonInfo);
        consoleElement.appendChild(item);
    });
  }

  // Call this after getting the result from the backend
  function handleAutoPlacementResult(result) {
    // ...existing code to handle arrangement...
    
    // Display explanations if available
    if (result.placementExplanations) {
        displayPlacementExplanations(result.placementExplanations);
    }
  }

  return (
    <div className="flex bg-gray-50 max-w-full overflow-x-hidden">
      {/* Global loading overlay with built-in animated spinner */}
      {isPacking && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-80 text-center" role="status" aria-live="polite">
            {/* Animated SVG spinner (no external deps) */}
            <svg width="72" height="72" viewBox="0 0 50 50" className="mx-auto" aria-hidden="true">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="#2563EB"
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray="31.4 31.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
            <div className="mt-4 text-gray-700 font-medium">{loadingMessage}</div>
            <div className="mt-1 text-gray-400 text-sm">Please wait...</div>
          </div>
        </div>
      )}
  {/* Main Content Area: only 3D Scene and overlays */}
  <div className="flex-1 flex flex-col bg-gray-50 relative overflow-hidden">
    {/* 3D Scene Area */}
  <div className="relative bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900"
          style={{ minHeight: 0, height: '62vh' }}
          onDragOver={onSceneDragOver}
          onDrop={onSceneDrop}
          onDragLeave={onSceneDragLeave}
        >
          {/* Non-intrusive overlay host to prevent layout jumps */}
          {showHazardBanner && (
            <div className="pointer-events-none absolute inset-0 z-50">
              <div className="pointer-events-auto absolute right-0 top-4 max-w-md bg-red-600 text-white px-4 py-2 rounded shadow-md text-sm relative pr-10">
                <div className="font-semibold">Safety warning</div>
                <div>{hazardWarnings[0].message}</div>
                {hazardWarnings.length > 1 && (
                  <div className="text-red-100 text-xs mt-1">+{hazardWarnings.length - 1} more</div>
                )}
                <button
                  aria-label="Dismiss warning"
                  title="Dismiss"
                  onClick={() => setDismissedHazardKey(hazardKey)}
                  className="absolute top-1 right-1 p-1 rounded hover:bg-red-700/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {isBoundaryWarning && (
            <div className="pointer-events-none absolute inset-0 z-40">
              <div className="pointer-events-auto absolute right-0 top-4 mr-2 mt-2 max-w-md bg-yellow-600 text-white px-3 py-2 rounded shadow-md text-sm">
                {error}
              </div>
            </div>
          )}
          {/* Quick actions toolbar - hidden on mobile, controls are in DeviasKit bottom menu */}
          <div className="hidden lg:flex absolute top-4 left-4 z-40 gap-2">
            <div className="flex items-center gap-2">
              <select
                value={demoDatasetKey}
                onChange={(e) => setDemoDatasetKey(e.target.value)}
                disabled={isPacking}
                className="px-2 py-1.5 text-sm rounded-md border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50 shadow-sm"
                title="Choose demo dataset"
              >
                <option value="full">Full Demo</option>
                <option value="variant_a">Variant A</option>
                <option value="variant_b">Variant B</option>
              </select>
              <button
                onClick={() => importStackedDataset(demoDatasetKey)}
                className="px-3 py-1.5 text-sm rounded-md border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50 shadow-sm"
                title="Load selected demo pallets"
                disabled={isPacking}
              >
                Load Demo
              </button>
            </div>
            {(() => {
              const canLockAny = (currentPallets || []).some(p => Array.isArray(p?.position) && p.position.length === 3 && !p.locked);
              return (
                <button
                  onClick={lockAllInTruck}
                  className={`px-3 py-1.5 text-sm rounded-md border ${canLockAny ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50' : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'} shadow-sm`}
                  title="Lock all placed pallets in this vehicle"
                  disabled={!canLockAny}
                >
                  Lock Placed
                </button>
              );
            })()}
            <button
              onClick={runGreedyPlacement}
              className="px-3 py-1.5 text-sm rounded-md border border-blue-300 bg-white text-blue-700 hover:bg-blue-50 shadow-sm"
              title="Run fast greedy placement (v2)"
              disabled={isPacking}
            >
              Greedy Place
            </button>
            <button
              onClick={runAutoPlacement}
              className="px-3 py-1.5 text-sm rounded-md border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50 shadow-sm"
              title="Run full optimizer (timeboxed)"
              disabled={isPacking}
            >
              Full Optimize
            </button>
            <button
              onClick={runStackedV2Placement}
              className="px-3 py-1.5 text-sm rounded-md border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 shadow-sm"
              title="Run experimental stacked solver"
              disabled={isPacking}
            >
              Stack V2 Test
            </button>
            {lastAlgorithm && (
              <span className="px-2 py-1 text-xs rounded bg-gray-800 text-white self-center select-none" title="Last algorithm used">
                {lastAlgorithm}
              </span>
            )}
          </div>
          {/* Action toast */}
          {actionToast && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 px-3 py-1.5 rounded bg-black/80 text-white text-sm shadow">
              {actionToast}
            </div>
          )}
          <Canvas
            dpr={isMobile ? [1, 1] : [1.5, 2]}
            gl={{ antialias: !isMobile, alpha: true, powerPreference: isMobile ? 'low-power' : 'high-performance' }}
            shadows={!isMobile}
            camera={{ position: [3.5, 3, 12], fov: 55 }}
            style={{ width: "100%", height: "100%" }}
            onPointerMissed={() => setSelectedPalletId(null)}
          >
            {isMobile && <AdaptiveDpr pixelated />}
            <ambientLight intensity={0.4} />
            <directionalLight
              castShadow={!isMobile}
              position={[5, 5, 5]}
              shadow-mapSize-width={isMobile ? 512 : 2048}
              shadow-mapSize-height={isMobile ? 512 : 2048}
              intensity={0.7}
            />
            <OrbitControls
              enablePan
              enableZoom
              enableRotate
              enabled={cameraActive && !dndActive}
              minDistance={isMobile ? 4 : 3}
              maxDistance={isMobile ? 20 : 30}
              target={[0, 1.3, 0]}
            />
            
            {/* Truck/Trailer Body */}
            <Truck vehicleType={vehicleType || currentTruck.vehicleType} />
            
            {/* Ghost (unplaced) pallets - only render in explicit ghost mode */}
            {showGhostsOnly && (unplacedPallets || []).filter(p => {
              const pos = p.position || [p.position?.x, p.position?.y, p.position?.z];
              const hasValid = Array.isArray(pos) ? pos.length === 3 : (p.position?.x != null && p.position?.y != null && p.position?.z != null);
              return hasValid;
            }).map((p, idx) => (
              <group
                key={`ghost-${p.id}-${idx}`}
                position={Array.isArray(p.position) ? p.position : [
                  (p.position?.[0] ?? p.position?.x ?? 0),
                  (p.position?.[1] ?? p.position?.y ?? 0),
                  (p.position?.[2] ?? p.position?.z ?? 0),
                ]}
                rotation={[0, ((p.rotation || 0) * Math.PI) / 180, 0]}
              >
                {/* Filled translucent body */}
                <mesh raycast={() => null}>
                  <boxGeometry args={[
                    p.dimensions?.width ?? 0.8,
                    p.dimensions?.height ?? 0.5,
                    p.dimensions?.depth ?? 1.2,
                  ]} />
                  <meshStandardMaterial
                    color={ATTRIBUTE_COLORS[(p.attribute || 'none').toLowerCase()] || '#999999'}
                    transparent
                    opacity={0.25}
                    depthWrite={false}
                  />
                </mesh>
                {/* Wireframe outline */}
                <mesh raycast={() => null}>
                  <boxGeometry args={[
                    p.dimensions?.width ?? 0.8,
                    p.dimensions?.height ?? 0.5,
                    p.dimensions?.depth ?? 1.2,
                  ]} />
                  <meshStandardMaterial color="#000000" wireframe transparent opacity={0.4} depthWrite={false} />
                </mesh>
              </group>
            ))}

            {/* All Placed Pallets */}
            {currentPallets.map((pallet, idx) => {
              const isNext = nextPalletId && pallet.id === nextPalletId;
              // In loading mode, only show already placed (locked) pallets and the next one
              const visibleMask = loadingMode ? ((pallet.locked === true) || !!isNext) : true;
              return (
                <Pallet
                  key={pallet.id}
                  index={idx}
                  id={pallet.id}
                  pallets={currentPallets}
                  position={pallet.position}
                  rotation={pallet.rotation || 0}
                  dimensions={pallet.dimensions}
                  attribute={pallet.attribute}
                  notToploadable={pallet.notToploadable}
                  name={pallet.name}
                  locked={pallet.locked || false}
                  onDrag={handleDrag}
                  setCameraActive={setCameraActive}
                  setSelectedPalletId={setSelectedPalletId}
                  selectedPalletId={selectedPalletId}
                  showDangerousOnly={showDangerousOnly}
                  vehicleType={currentTruck.vehicleType || 'trailer'}
                  fused={pallet.fused || false}
                  smartSnapEnabled={smartSnapEnabled}
                  onMoveError={showMoveError}
                  overrideVisible={visibleMask}
                  highlight={loadingMode && !!isNext}
                />
              );
            })}
            
            {/* Live drop preview while dragging from Unplaced list */}
            {dndActive && dndPalletId && dndScreenPos && (
              <DropPreview
                screen={dndScreenPos}
                pallet={getUnplacedById(dndPalletId)}
              />
            )}

            {/* Grid Lines */}
            {enableGrid && <Grid />}
          </Canvas>
          {error && (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-6 z-50 bg-red-600 text-white px-4 py-2 rounded shadow-md text-sm">
              {error}
            </div>
          )}
        </div>
      </div>
      {/* Manifest Modal */}
      {manifestOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={()=>setManifestOpen(false)} />
          <div className="relative z-[121] w-[44rem] max-w-[95vw] bg-white border border-gray-200 shadow-2xl rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-gray-800">
                Loading Manifest{(() => { try { const j = JSON.parse(manifestText||'{}'); return j.vehicleName ? ` - ${j.vehicleName}` : ''; } catch { return ''; } })()}
              </div>
              <button onClick={()=>setManifestOpen(false)} className="p-1 rounded hover:bg-gray-100 text-gray-500" title="Close">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="mb-3">
              <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-auto max-h-[50vh]">{manifestText}</pre>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={()=>{ navigator.clipboard?.writeText(manifestText); }} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Copy</button>
              <button onClick={downloadManifest} className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700">Download JSON</button>
                <button
                  onClick={async () => {
                    try {
                      const parsed = JSON.parse(manifestText || '{}');
                      // Attach a primary vehicle/trailer name hint for PDF naming
                      try {
                        if (!parsed._exportVehicleName) {
                          if (parsed.vehicleName) {
                            parsed._exportVehicleName = parsed.vehicleName;
                          } else if (currentTruck && currentTruck.name) {
                            parsed._exportVehicleName = currentTruck.name;
                          }
                        }
                      } catch {}
                      await generateManifestPdf(parsed);
                    } catch (err) {
                      alert("PDF export failed: " + err.message);
                    }
                  }}
                  className="px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700"
                >
                  Export PDF
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default TruckLoadingScene;